import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import AutoComplete from '../components/AutoComplete';
import ConfirmDestructive from '../components/ConfirmDestructive';
import { useAuth } from '../context/AuthContext';
import {
  requerimientoService, serieModuloService, serieService,
  sucursalService, trabajadorService, entidadService,
  tablaService, articuloService, unidadMedidaService,
  datosGeneralesService, ordenCompraService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import { useCatalogSync } from '../utils/eventBus';
import '../styles/datatable.css';
import '../styles/articulos.css';
import '../styles/requerimientos.css';

const CATALOG_TOPICS_RQ = [
  'entidades', 'articulos', 'trabajadores', 'areas', 'prioridades',
  'centros-costo', 'tipos-documento', 'unidades-medida', 'sucursales',
  'series', 'modulos', 'marcas', 'familias', 'grupos', 'lineas',
  'categorias', 'tipos-existencia', 'cuentas-contables', 'modelos',
];

const today   = () => new Date().toISOString().split('T')[0];
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; };

const ESTADO_COLORS = {
  PENDIENTE:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
  APROBADO:    { color: '#22c55e', bg: 'rgba(34,197,94,0.1)'   },
  DESAPROBADO: { color: '#fb923c', bg: 'rgba(251,146,60,0.1)'  },
  ANULADO:     { color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
};

const TIPOS_DOC_ENTIDAD = [
  { codigo: '6', label: 'RUC' }, { codigo: '1', label: 'DNI' },
  { codigo: '4', label: 'Carnet de Extranjería' }, { codigo: '7', label: 'Pasaporte' },
  { codigo: '0', label: 'Otros' },
];

// Columnas de la plantilla Excel de importación masiva de RQ.
// Cada fila es un ítem; el agrupador natural es (Sucursal, Serie, Numero).
const RQ_IMPORT_COLUMNS = [
  { key: 'Sucursal',            label: 'Sucursal',            required: true,  example: 'PRIN',        description: 'Código o nombre de la sucursal' },
  { key: 'Serie',               label: 'Serie',               required: true,  example: '0001',        description: 'Serie RQ de 4 dígitos (sin prefijo)' },
  { key: 'Numero',              label: 'Numero',              required: true,  example: '42',          description: 'Número correlativo del RQ (entero)' },
  { key: 'Fecha_Emision',       label: 'Fecha_Emision',       required: true,  example: '2026-04-21',  description: 'YYYY-MM-DD o fecha Excel' },
  { key: 'Fecha_Entrega',       label: 'Fecha_Entrega',       required: false, example: '2026-05-05',  description: 'Opcional, YYYY-MM-DD' },
  { key: 'Codigo_Area',         label: 'Codigo_Area',         required: true,  example: 'PROD',        description: 'Código del área' },
  { key: 'DNI_Solicitante',     label: 'DNI_Solicitante',     required: true,  example: '12345678',    description: 'DNI del trabajador solicitante' },
  { key: 'Codigo_Prioridad',    label: 'Codigo_Prioridad',    required: true,  example: '02',          description: 'Código de prioridad (p.ej. 02 = Normal)' },
  { key: 'RUC_Proveedor',       label: 'RUC_Proveedor',       required: false, example: '20123456789', description: 'RUC del proveedor sugerido (opcional)' },
  { key: 'Observacion_Cabecera',label: 'Observacion_Cabecera',required: false, example: 'Compra mensual', description: 'Observación general del requerimiento (opcional)' },
  { key: 'Estado',              label: 'Estado',              required: false, example: 'PENDIENTE',   description: 'PENDIENTE | APROBADO | DESAPROBADO | ANULADO (default PENDIENTE; para migración histórica)' },
  { key: 'Codigo_Articulo',     label: 'Codigo_Articulo',     required: true,  example: 'ART001',      description: 'Código interno del artículo' },
  { key: 'Cantidad',            label: 'Cantidad',            required: true,  example: '5',           description: 'Cantidad solicitada (> 0)' },
  { key: 'Codigo_Centro_Costo', label: 'Codigo_Centro_Costo', required: false, example: 'CC-01',       description: 'Código del centro de costo por ítem (opcional)' },
  { key: 'Observacion_Item',    label: 'Observacion_Item',    required: false, example: 'Urgente',     description: 'Observación del ítem (opcional)' },
];

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════
// Modal de Firmantes — 3 roles fijos con AutoComplete de trabajadores
// ═══════════════════════════════════════════════════════════════════
const ROLES_FIRMA = ['Autoriza', 'Atiende', 'Recibe'];
const EMPTY_SLOT  = { id_trabajador: '', nombres: '', dni: '', cargo: '' };
const LS_KEY      = 'rq_firmantes_default';

// ── InputFirmante ────────────────────────────────────────────────
// Autocomplete dedicado para firmantes. zIndex 12000 para quedar
// por encima del modal de firmantes (zIndex 11000).
const HIDDEN_STYLE = { position: 'fixed', top: -9999, left: -9999, visibility: 'hidden' };

const InputFirmante = ({ value, lista, onChange, onAdd }) => {
  const [open,        setOpen]        = useState(false);
  const [search,      setSearch]      = useState('');
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef     = useRef(null);
  const dropRef      = useRef(null);
  const containerRef = useRef(null);
  // Empieza off-screen con visibility:hidden — NUNCA como {} vacío
  const [dropStyle, setDropStyle] = useState(HIDDEN_STYLE);

  /* ── Filtro ──────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return lista
      .filter(t =>
        (t.nombres || '').toLowerCase().includes(q) ||
        (t.dni     || '').includes(q) ||
        (t.cargo   || '').toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [lista, search]);

  /* ── Calcular posición (zIndex 12000 > modal 11000) ─────────── */
  const calcPos = useCallback(() => {
    if (!inputRef.current) return;
    const rect      = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp     = spaceBelow < 260 && rect.top > 260;
    setDropStyle({
      position   : 'fixed',
      left       : rect.left,
      top        : openUp ? 'auto'                              : rect.bottom + 4,
      bottom     : openUp ? (window.innerHeight - rect.top + 4) : 'auto',
      minWidth   : Math.max(rect.width, 220),
      maxWidth   : Math.min(640, window.innerWidth - rect.left - 12),
      width      : 'max-content',
      zIndex     : 12000,
      visibility : 'visible',
    });
  }, []);

  /* ── Efecto open/close ───────────────────────────────────────── */
  useEffect(() => {
    if (open) {
      calcPos();
      window.addEventListener('scroll', calcPos, true);
      window.addEventListener('resize', calcPos);
      return () => {
        window.removeEventListener('scroll', calcPos, true);
        window.removeEventListener('resize', calcPos);
        setDropStyle(HIDDEN_STYLE);   // ocultar al cerrar
      };
    }
  }, [open, calcPos]);

  /* ── Cerrar al clic fuera ────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (
        !containerRef.current?.contains(e.target) &&
        !dropRef.current?.contains(e.target)
      ) {
        setOpen(false);
        if (!value) setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value]);

  /* ── Acciones ────────────────────────────────────────────────── */
  const select = (t) => {
    onChange(t);
    setSearch('');
    setOpen(false);
    setHighlighted(-1);
  };

  const clear = () => {
    onChange(null);
    setSearch('');
    setHighlighted(-1);
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (!open) { if (e.key !== 'Escape') setOpen(true); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, -1));
    } else if ((e.key === 'Enter' || e.key === 'Tab') && highlighted >= 0 && filtered[highlighted]) {
      e.preventDefault();
      select(filtered[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  };

  /* ── Scroll automático al item resaltado ─────────────────────── */
  useEffect(() => {
    if (highlighted >= 0 && dropRef.current) {
      dropRef.current.children[highlighted]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted]);

  /* ── Resaltar texto coincidente ──────────────────────────────── */
  const hl = (text, q) => {
    if (!q || !text) return text;
    const i = (text + '').toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;
    return (
      <>{text.slice(0, i)}
        <strong style={{ color: '#60a5fa', fontWeight: 700 }}>{text.slice(i, i + q.length)}</strong>
        {text.slice(i + q.length)}
      </>
    );
  };

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <div ref={containerRef}>
      {/* Input / display seleccionado */}
      <div style={{
        display   : 'flex',
        alignItems: 'center',
        border    : `1px solid ${open ? 'rgba(96,165,250,0.5)' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 6,
        background: value ? 'rgba(96,165,250,0.08)' : 'rgba(255,255,255,0.04)',
        transition: 'border-color 0.15s',
        minHeight : 38,
      }}>
        {value ? (
          <div style={{ flex: 1, padding: '6px 10px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#c8d8e8' }}>{value.nombres}</div>
            <div style={{ fontSize: 11, color: '#8899aa', marginTop: 1 }}>
              {value.dni   && <span>DNI: {value.dni}</span>}
              {value.cargo && <span style={{ marginLeft: value.dni ? 8 : 0 }}>{value.cargo}</span>}
            </div>
          </div>
        ) : (
          <input
            ref={inputRef}
            value={search}
            onChange={e => { setSearch(e.target.value); setHighlighted(-1); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar por nombre, DNI o cargo..."
            autoComplete="off"
            style={{
              flex: 1, border: 'none', background: 'transparent',
              outline: 'none', color: '#c8d8e8', fontSize: 13, padding: '8px 10px',
            }}
          />
        )}
        {value && (
          <button type="button" onClick={clear}
            style={{ background:'none', border:'none', color:'#8899aa', cursor:'pointer', padding:'0 8px', fontSize:20, lineHeight:1, flexShrink:0 }}>
            ×
          </button>
        )}
        <button type="button" onClick={onAdd} title="Agregar nuevo trabajador"
          style={{
            background:'none', border:'none',
            borderLeft:'1px solid rgba(255,255,255,0.08)',
            color:'#8899aa', cursor:'pointer', padding:'0 11px',
            alignSelf:'stretch', fontSize:18, flexShrink:0,
            display:'flex', alignItems:'center',
          }}>+</button>
      </div>

      {/* Portal — siempre montado, visible solo cuando open (evita flash de layout) */}
      {ReactDOM.createPortal(
        <div ref={dropRef} style={{
          ...dropStyle,
          background  : '#0f1b2d',
          border      : '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8,
          boxShadow   : '0 8px 32px rgba(0,0,0,0.55)',
          maxHeight   : 260,
          overflowY   : 'auto',
          overflowX   : 'hidden',
        }}>
          {open && (
            filtered.length === 0
              ? <div style={{ padding:'10px 14px', color:'#8899aa', fontSize:12 }}>No se encontraron resultados</div>
              : filtered.map((t, idx) => (
                <div key={t.id_trabajador}
                  onMouseDown={() => select(t)}
                  onMouseEnter={() => setHighlighted(idx)}
                  style={{
                    padding    : '8px 14px',
                    cursor     : 'pointer',
                    background : idx === highlighted ? 'rgba(96,165,250,0.12)' : 'transparent',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    whiteSpace : 'nowrap',
                    transition : 'background 0.1s',
                  }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#c8d8e8' }}>
                    {hl(t.nombres || '', search)}
                  </div>
                  <div style={{ fontSize:10.5, color:'#8899aa', marginTop:2 }}>
                    {t.dni   && <span>{hl('DNI: ' + t.dni, search)}</span>}
                    {t.cargo && <span style={{ marginLeft: t.dni ? 8 : 0 }}>{hl(t.cargo || '', search)}</span>}
                  </div>
                </div>
              ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// ── ModalFirmantes ───────────────────────────────────────────────
const ModalFirmantes = ({ trabajadores: trabajadoresInic, initialFirmantes, onConfirm, onCancel }) => {
  // Lista local: puede crecer si se agrega un trabajador nuevo
  const [lista, setLista] = useState(trabajadoresInic);
  // Sincronizar si el prop llega después del primer render
  useEffect(() => { setLista(trabajadoresInic); }, [trabajadoresInic]);

  // Slots: objeto completo por rol
  const [slots, setSlots] = useState(() =>
    ROLES_FIRMA.map((_, i) => ({ ...EMPTY_SLOT, ...(initialFirmantes[i] || {}) }))
  );
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  // Mini-formulario para agregar trabajador rápido
  const [addIdx, setAddIdx]         = useState(null); // índice del slot que lo abrió
  const [addForm, setAddForm]       = useState({ nombres: '', dni: '', cargo: '' });
  const [savingAdd, setSavingAdd]   = useState(false);
  const [addError, setAddError]     = useState('');

  const handleSaveAdd = async () => {
    if (!addForm.nombres.trim()) { setAddError('Nombres es obligatorio.'); return; }
    setSavingAdd(true); setAddError('');
    try {
      const res = await trabajadorService.create({
        nombres: addForm.nombres.trim(),
        dni    : addForm.dni.trim()   || null,
        cargo  : addForm.cargo.trim() || null,
      });
      const nuevo = res.data;
      setLista(prev => [...prev, nuevo]);
      if (addIdx !== null) {
        setSlots(prev => prev.map((s, i) =>
          i === addIdx ? { id_trabajador: nuevo.id_trabajador, nombres: nuevo.nombres, dni: nuevo.dni || '', cargo: nuevo.cargo || '' } : s
        ));
      }
      setAddIdx(null);
      setAddForm({ nombres: '', dni: '', cargo: '' });
    } catch {
      setAddError('Error al guardar el trabajador.');
    } finally { setSavingAdd(false); }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 11000 }}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15 }}>Firmantes del Requerimiento</h3>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="modal-body" style={{ paddingBottom: 8 }}>
          <p style={{ color:'#8899aa', fontSize:12, marginBottom:16 }}>
            Seleccione los trabajadores que firmarán el documento impreso.
          </p>

          {ROLES_FIRMA.map((rol, i) => (
            <div key={rol} style={{ marginBottom: 14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#8899aa', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:5 }}>
                Quien {rol}
              </div>
              <InputFirmante
                value={slots[i].id_trabajador ? slots[i] : null}
                lista={lista}
                onChange={(t) => setSlots(prev => prev.map((s, idx) =>
                  idx === i ? (t ? { id_trabajador: t.id_trabajador, nombres: t.nombres, dni: t.dni || '', cargo: t.cargo || '' } : { ...EMPTY_SLOT }) : s
                ))}
                onAdd={() => { setAddIdx(i); setAddForm({ nombres:'', dni:'', cargo:'' }); setAddError(''); }}
              />
            </div>
          ))}

          {/* Mini-form agregar trabajador */}
          {addIdx !== null && (
            <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'12px 14px', marginTop:4 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#c8d8e8', marginBottom:10 }}>
                Nuevo trabajador — para «Quien {ROLES_FIRMA[addIdx]}»
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 12px' }}>
                <div className="form-row">
                  <label>Nombres *</label>
                  <input autoFocus value={addForm.nombres} onChange={e => setAddForm(p => ({ ...p, nombres: e.target.value }))} placeholder="Nombres completos" />
                </div>
                <div className="form-row">
                  <label>DNI</label>
                  <input value={addForm.dni} onChange={e => setAddForm(p => ({ ...p, dni: e.target.value }))} placeholder="Nro. documento" />
                </div>
              </div>
              <div className="form-row">
                <label>Cargo</label>
                <input value={addForm.cargo} onChange={e => setAddForm(p => ({ ...p, cargo: e.target.value }))} placeholder="Ej: Jefe de Almacén" />
              </div>
              {addError && <div style={{ color:'#ef4444', fontSize:12, marginBottom:6 }}>{addError}</div>}
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
                <button className="btn btn-secondary" type="button" onClick={() => setAddIdx(null)} style={{ fontSize:12, padding:'4px 12px' }}>Cancelar</button>
                <button className="btn btn-primary"   type="button" onClick={handleSaveAdd} disabled={savingAdd} style={{ fontSize:12, padding:'4px 12px' }}>
                  {savingAdd ? 'Guardando...' : 'Guardar y seleccionar'}
                </button>
              </div>
            </div>
          )}

          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginTop:14, color:'#8899aa', fontSize:12 }}>
            <input type="checkbox" checked={saveAsDefault} onChange={e => setSaveAsDefault(e.target.checked)}
              style={{ accentColor:'#3b82f6', width:14, height:14 }} />
            Guardar como valores por defecto para futuras impresiones
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
          <button className="btn btn-primary" onClick={() => onConfirm(slots, saveAsDefault)}>Imprimir</button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
const Requerimientos = () => {
  const { sucursal: sesionSucursal } = useAuth();

  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState('list'); // 'list' | 'form'
  const [editingId, setEditingId] = useState(null);

  // ── Catálogos ────────────────────────────────────────────────────
  const [tiposDocSerie, setTiposDocSerie]           = useState([]);
  const [series, setSeries]                         = useState([]);
  const [sucursales, setSucursales]                 = useState([]);
  const [areas, setAreas]                           = useState([]);
  const [trabajadores, setTrabajadores]             = useState([]);
  const [prioridades, setPrioridades]               = useState([]);
  const [proveedores, setProveedores]               = useState([]);
  const [centrosCosto, setCentrosCosto]             = useState([]);
  const [tiposDocComerciales, setTiposDocComerciales] = useState([]);
  const [articulos, setArticulos]                   = useState([]);
  // Catálogos para modal artículo
  const [unidades, setUnidades]     = useState([]);
  const [marcas, setMarcas]         = useState([]);
  const [modelos, setModelos]       = useState([]);
  const [familias, setFamilias]     = useState([]);
  const [grupos, setGrupos]         = useState([]);
  const [lineas, setLineas]         = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tiposExistencia, setTiposExistencia] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);

  // ── Formulario cabecera ──────────────────────────────────────────
  const emptyForm = {
    id_sucursal: sesionSucursal?.id_sucursal || '',
    tipo_documento: 'RQ', id_serie: '', numero: '',
    fecha_emision: today(), id_area: '', id_solicitante: '',
    id_prioridad: '', id_proveedor: '', observacion1: '', observacion2: '',
    id_centro_costo: '', fecha_entrega: today(),
    id_tipo_doc_ref1: '', num_doc_ref1: '', id_tipo_doc_ref2: '', num_doc_ref2: '',
  };
  const [form, setForm]       = useState(emptyForm);
  const [detalle, setDetalle] = useState([]);

  // ── Notificaciones ───────────────────────────────────────────────
  const [alert, setAlert]     = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast]     = useState(null);

  // ── Modal Proveedor ──────────────────────────────────────────────
  const [modalProv, setModalProv]           = useState(false);
  const [savingProv, setSavingProv]         = useState(false);
  const [consultandoRuc, setConsultandoRuc] = useState(false);
  const emptyProv = { tipo_documento: '6', ruc: '', razon_social: '', direccion: '', correo1: '', telefono_contacto: '', celular: '', persona_contacto: '' };
  const [formProv, setFormProv] = useState(emptyProv);

  // ── Modal Artículo ───────────────────────────────────────────────
  const [modalArt, setModalArt]     = useState(false);
  const [savingArt, setSavingArt]   = useState(false);
  const [artTab, setArtTab]         = useState('general');
  const [autoCode, setAutoCode]     = useState(true);
  const [artLineaKey, setArtLineaKey] = useState(null);
  const setArtField = (field, value) => setFormArt(prev => ({ ...prev, [field]: value }));
  const emptyArt = {
    codigo_interno: '', codigo_sunat: '', descripcion: '', descripcion_alt: '',
    id_unidad: '', peso: '', id_marca: '', id_modelo: '', id_familia: '', id_grupo: '',
    id_linea: '', id_categoria: '', pais: '', id_cuenta_contable: '', id_tipo_existencia: '',
    ancho: '', largo: '', grosor: '', volumen: '', area: '', medida: '',
    stock_minimo: '', stock_maximo: '',
  };
  const [formArt, setFormArt] = useState(emptyArt);

  // ── Mini Modal (creación rápida de subcategorías de artículo) ────
  const [miniModal, setMiniModal] = useState(null); // { type, label }
  const [miniForm, setMiniForm]   = useState({ codigo: '', descripcion: '', codigo_sunat: '' });

  // ── Modal Centro de Costo ────────────────────────────────────────
  const [modalCC, setModalCC]   = useState(false);
  const [savingCC, setSavingCC] = useState(false);
  const [formCC, setFormCC]     = useState({ codigo: '', descripcion: '' });

  // ── Modal Desaprobar (elección de estado) ────────────────────────
  const [desaprobarTarget, setDesaprobarTarget] = useState(null);

  // ── Modal Ver RQ ──────────────────────────────────────────────────
  const [viewModal, setViewModal]   = useState(null); // row
  const [viewData, setViewData]     = useState(null); // { req, atencion }
  const [loadingView, setLoadingView] = useState(false);

  // ── Modal Atención OC ─────────────────────────────────────────────
  const [atencionOCModal, setAtencionOCModal] = useState(null); // { rq } | null
  const [atencionOCData, setAtencionOCData]   = useState(null); // { items, ocs }
  const [loadingAtencionOC, setLoadingAtencionOC] = useState(false);

  // ── Filtros de lista ─────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ serie: '', numero: '', fechaInicio: weekAgo(), fechaFin: today(), estado: '', idArea: '', idSucursal: '', idArticulo: '', codigoArticulo: '', articuloDescripcion: '', idCentroCosto: '', codigoCentroCosto: '' });
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const importRef   = useRef(null);
  const templateRef = useRef(null);
  const exportRef   = useRef(null);
  const importMenuRef = useRef(null);

  // ── Modal Firmantes (pre-impresión) ──────────────────────────────
  const [printTarget, setPrintTarget] = useState(null);
  const [seguimiento, setSeguimiento] = useState(null);          // { rq, ocs, pes_origen, grs, pes_destino }
  const [loadingSeguimiento, setLoadingSeguimiento] = useState(false);
  const [segTab, setSegTab] = useState('oc');
  const [segChoice, setSegChoice] = useState(null);              // { r } — selector Resumen/Detallado

  const abrirSeguimiento = (r) => setSegChoice(r);

  const verSegResumen = async () => {
    const r = segChoice;
    if (!r) return;
    setSegChoice(null);
    setSegTab('oc');
    setSeguimiento({ loading: true, id_rq: r.id_requerimiento, rq_numero: r.numero, rq_serie: r.serie_codigo });
    setLoadingSeguimiento(true);
    try {
      const res = await requerimientoService.getSeguimiento(r.id_requerimiento);
      setSeguimiento({ ...res.data, loading: false });
    } catch {
      setAlert({ type: 'error', message: 'No se pudo cargar el seguimiento.' });
      setSeguimiento(null);
    } finally { setLoadingSeguimiento(false); }
  };

  const verSegDetallado = () => {
    const r = segChoice;
    if (!r) return;
    setSegChoice(null);
    window.open(`/requerimientos/seguimiento-detallado/${r.id_requerimiento}`, '_blank', 'noopener,noreferrer');
  };
  const [firmantesDefault, setFirmantesDefault] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; }
  });

  // ─────────────────────────────────────────────────────────────────
  // Carga de datos
  // ─────────────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try { const r = await requerimientoService.getAll(); setRecords(r.data); }
    catch { setAlert({ type: 'error', message: 'Error al cargar requerimientos.' }); }
    finally { setLoading(false); }
  }, []);

  const fetchCatalogos = useCallback(async () => {
    try {
      // Nota: los artículos se buscan remotamente (AutoComplete async), ya no se precargan.
      const [
        tiposSerie, ser, suc, ar, trab, prior, prov, cc, tiposComerciales,
        uni, mar, fam, gru, lin, cat, te, cuc, mod,
      ] = await Promise.all([
        serieModuloService.getAll(), serieService.getAll(null, sesionSucursal?.id_sucursal),
        sucursalService.getAll(), tablaService('areas').getAll(),
        trabajadorService.getAll(), tablaService('prioridades').getAll(),
        entidadService.getAll(), tablaService('centros-costo').getAll(),
        tablaService('tipos-documento').getAll(),
        unidadMedidaService.getAll(), tablaService('marcas').getAll(),
        tablaService('familias').getAll(), tablaService('grupos').getAll(),
        tablaService('lineas').getAll(), tablaService('categorias').getAll(),
        tablaService('tipos-existencia').getAll(), tablaService('cuentas-contables').getAll(),
        tablaService('modelos').getAll(),
      ]);
      setTiposDocSerie(tiposSerie.data);
      setSeries(ser.data);
      setSucursales(suc.data);
      setAreas(ar.data);
      setTrabajadores(trab.data.map(t => ({
        ...t,
        codigo: t.dni || '',
        display_trab: t.dni ? `${t.dni} — ${t.nombres}` : t.nombres,
      })));
      const priorData = prior.data;
      setPrioridades(priorData);
      setProveedores(prov.data.map(e => ({
        ...e,
        codigo: e.ruc || '',
        display_prov: e.ruc ? `${e.ruc} — ${e.razon_social}` : (e.razon_social || ''),
      })));
      setCentrosCosto(cc.data.map(c => ({
        ...c,
        display_cc: c.descripcion,
      })));
      setTiposDocComerciales(tiposComerciales.data);
      // Los artículos se buscan remotamente; no se precarga el catálogo completo.
      setArticulos([]);
      setUnidades(uni.data);
      setMarcas(mar.data);
      setFamilias(fam.data);
      setGrupos(gru.data);
      setLineas(lin.data);
      setCategorias(cat.data);
      setTiposExistencia(te.data);
      setCuentasContables(cuc.data);
      setModelos(mod.data);
      const pNormal = priorData.find(p => p.codigo === '02');
      return { priorNormalId: pNormal ? String(pNormal.id_prioridad) : '', seriesData: ser.data };
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar catálogos.' });
      return { priorNormalId: '', seriesData: [] };
    }
  }, [sesionSucursal?.id_sucursal]);

  // Carga ligera para los autocompletes del panel de filtros
  const fetchFiltrosCatalogos = useCallback(async () => {
    try {
      const [ser, suc, ar, cc] = await Promise.all([
        serieService.getAll(null, sesionSucursal?.id_sucursal),
        sucursalService.getAll(),
        tablaService('areas').getAll(),
        tablaService('centros-costo').getAll(),
      ]);
      setSeries(ser.data);
      setSucursales(suc.data);
      setAreas(ar.data);
      setCentrosCosto((cc.data || []).map(c => ({ ...c, display_cc: c.descripcion })));
      // Los artículos se buscan remotamente en el filtro (AutoComplete async).
    } catch {}
  }, [sesionSucursal?.id_sucursal]);

  useEffect(() => { fetchRecords(); fetchFiltrosCatalogos(); }, [fetchRecords, fetchFiltrosCatalogos]);

  // Refetch automático cuando otros flujos mutan catálogos (crear proveedor, artículo, etc.)
  useCatalogSync(CATALOG_TOPICS_RQ, fetchCatalogos);
  useCatalogSync('requerimientos', fetchRecords);

  // ─── Importación masiva de RQ ────────────────────────────────────
  useEffect(() => {
    const HEADER_FIELDS = ['Fecha_Emision', 'Fecha_Entrega', 'Codigo_Area', 'DNI_Solicitante', 'Codigo_Prioridad', 'RUC_Proveedor', 'Observacion_Cabecera', 'Estado'];

    const normFecha = (v) => {
      if (v === null || v === undefined || v === '') return null;
      if (typeof v === 'number') {
        // Serial Excel → ISO
        const d = new Date(Math.round((v - 25569) * 86400 * 1000));
        return d.toISOString().split('T')[0];
      }
      const s = String(v).trim();
      if (!s) return null;
      return s.includes('T') ? s.split('T')[0] : s;
    };

    const normStr = (v) => (v === undefined || v === null) ? '' : String(v).trim();

    window._dtImportHandler = async (rows) => {
      if (!rows || rows.length === 0) return;

      // 1. Agrupar por Sucursal + Serie + Numero
      const groups = new Map();
      const keyOf = (r) => {
        const suc = normStr(r.Sucursal).toUpperCase();
        const ser = normStr(r.Serie).toUpperCase().padStart(4, '0');
        const num = parseInt(normStr(r.Numero).replace(/^0+/, '')) || 0;
        return `${suc}|${ser}|${num}`;
      };
      rows.forEach((row, i) => {
        const k = keyOf(row);
        if (!groups.has(k)) groups.set(k, { header: row, filas: [] });
        groups.get(k).filas.push({ row, idx: i });
      });

      // 2. Validar consistencia de cabecera dentro de cada grupo
      const inconsistencias = [];
      for (const [k, g] of groups) {
        const h = g.header;
        for (const { row, idx } of g.filas) {
          for (const f of HEADER_FIELDS) {
            const a = normStr(row[f]);
            const b = normStr(h[f]);
            if (a !== b) {
              inconsistencias.push(`Grupo ${k}: fila ${idx + 1} difiere en "${f}" ("${a}" vs. "${b}")`);
              break;
            }
          }
        }
      }

      // 3. Si hay inconsistencias, abortar la importación completa
      if (inconsistencias.length > 0) {
        const total = groups.size;
        let i = 0;
        for (const msg of inconsistencias) {
          i++;
          if (window._dtImportProgress) window._dtImportProgress(i, total, 0, i, msg);
        }
        if (window._dtImportProgress) window._dtImportProgress(total, total, 0, total, 'Importación abortada: corrige las inconsistencias y reintenta.');
        return;
      }

      // 4. Enviar cada grupo al backend
      let ok = 0, fail = 0;
      const total = groups.size;
      let procesados = 0;
      for (const [, g] of groups) {
        procesados++;
        const h = g.header;
        const payload = {
          codigo_sucursal:  normStr(h.Sucursal),
          serie:            normStr(h.Serie).padStart(4, '0'),
          numero:           normStr(h.Numero),
          fecha_emision:    normFecha(h.Fecha_Emision),
          fecha_entrega:    normFecha(h.Fecha_Entrega) || null,
          codigo_area:      normStr(h.Codigo_Area),
          dni_solicitante:  normStr(h.DNI_Solicitante),
          codigo_prioridad: normStr(h.Codigo_Prioridad),
          ruc_proveedor:    normStr(h.RUC_Proveedor) || null,
          observacion1:     normStr(h.Observacion_Cabecera) || null,
          estado:           normStr(h.Estado).toUpperCase() || null,
          items: g.filas.map(({ row }) => ({
            codigo_articulo:     normStr(row.Codigo_Articulo),
            cantidad:            parseFloat(row.Cantidad) || 0,
            codigo_centro_costo: normStr(row.Codigo_Centro_Costo) || null,
            observacion:         normStr(row.Observacion_Item) || null,
          })),
        };
        let errorMsg = null;
        try {
          await requerimientoService.importGrupo(payload);
          ok++;
        } catch (err) {
          fail++;
          errorMsg = `RQ ${payload.serie}-${payload.numero}: ${getErrorDetail(err, 'Error al importar')}`;
        }
        if (window._dtImportProgress) window._dtImportProgress(procesados, total, ok, fail, errorMsg);
      }
      fetchRecords();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchRecords]);

  // ─────────────────────────────────────────────────────────────────
  // Abrir / cerrar formulario
  // ─────────────────────────────────────────────────────────────────
  const openCreate = async () => {
    setEditingId(null);
    setDetalle([]);
    const { priorNormalId } = await fetchCatalogos();
    setForm({
      ...emptyForm,
      id_sucursal:  sesionSucursal?.id_sucursal || '',
      id_prioridad: priorNormalId,
      fecha_entrega: today(),
    });
    setView('form');
  };

  const openEdit = async (r) => {
    setEditingId(r.id_requerimiento);
    await fetchCatalogos();
    try {
      const res = await requerimientoService.getById(r.id_requerimiento);
      const d = res.data;
      setForm({
        id_sucursal:      d.id_sucursal      || '',
        tipo_documento:   d.tipo_documento   || '',
        id_serie:         d.id_serie         || '',
        numero:           d.numero           || '',
        fecha_emision:    d.fecha_emision    ? d.fecha_emision.split('T')[0] : today(),
        id_area:          d.id_area          || '',
        id_solicitante:   d.id_solicitante   || '',
        id_prioridad:     d.id_prioridad     || '',
        id_proveedor:     d.id_proveedor     || '',
        observacion1:     d.observacion1     || '',
        observacion2:     d.observacion2     || '',
        id_centro_costo:  d.id_centro_costo  || '',
        fecha_entrega:    d.fecha_entrega    ? d.fecha_entrega.split('T')[0] : today(),
        id_tipo_doc_ref1: d.id_tipo_doc_ref1 || '',
        num_doc_ref1:     d.num_doc_ref1     || '',
        id_tipo_doc_ref2: d.id_tipo_doc_ref2 || '',
        num_doc_ref2:     d.num_doc_ref2     || '',
      });
      setDetalle((d.detalle || []).map(l => ({
        _key: l.id_detalle,
        id_articulo: l.id_articulo || '',
        cantidad: l.cantidad || 1,
        observacion: l.observacion || '',
        id_centro_costo: l.id_centro_costo || '',
        // Datos cacheados en la línea para el AutoComplete remoto
        articulo_codigo: l.articulo_codigo || '',
        articulo_descripcion: l.articulo_descripcion || '',
        id_unidad: l.id_unidad || null,
        unidad_descripcion: l.unidad_descripcion || '',
      })));
      setView('form');
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar requerimiento.' });
    }
  };

  const closeForm = () => { setView('list'); setEditingId(null); };

  // ── Serie → auto número ──────────────────────────────────────────
  const handleSerieChange = (val) => {
    setForm(f => {
      const updated = { ...f, id_serie: val };
      if (val && !editingId) {
        const s = series.find(x => String(x.id_serie) === String(val));
        if (s) {
          const next = (s.ultimo_numero || 0) + 1;
          updated.numero = String(next).padStart(s.maximo_numeros || 7, '0');
        }
      }
      return updated;
    });
  };

  const handleTipoDocChange = (val) =>
    setForm(f => ({ ...f, tipo_documento: val, id_serie: '', numero: '' }));

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Cambia el CC de cabecera y lo propaga a todos los ítems del detalle.
  // El usuario puede cambiar luego el CC de una línea individual y ese valor se respeta.
  const handleCCCabeceraChange = (v) => {
    setForm(p => ({ ...p, id_centro_costo: v || '' }));
    setDetalle(prev => prev.map(l => ({ ...l, id_centro_costo: v || '' })));
  };

  // ── Detalle ──────────────────────────────────────────────────────
  // Al agregar línea hereda el CC de cabecera si existe (el usuario puede cambiarlo por línea).
  const addLinea    = () => setDetalle(p => [...p, {
    _key: Date.now(), id_articulo: '', cantidad: 1, observacion: '',
    id_centro_costo: form?.id_centro_costo || '',
    articulo_codigo: '', articulo_descripcion: '', id_unidad: null, unidad_descripcion: '',
  }]);
  const removeLinea = k  => setDetalle(p => p.filter(l => l._key !== k));
  const updateLinea = (k, field, val) => setDetalle(p => p.map(l => l._key === k ? { ...l, [field]: val } : l));
  // Al seleccionar un artículo via búsqueda remota, guardamos código/descripción/unidad en la línea
  const seleccionarArticuloLinea = (k, art) => {
    setDetalle(p => p.map(l => l._key === k ? {
      ...l,
      id_articulo: art?.id_articulo || '',
      articulo_codigo: art?.codigo_interno || '',
      articulo_descripcion: art?.descripcion || '',
      id_unidad: art?.id_unidad || null,
      unidad_descripcion: art?.unidad_descripcion || '',
    } : l));
  };

  // ── Guardar ──────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_serie)      { setAlert({ type: 'warning', message: 'La serie es obligatoria.' }); return; }
    if (!form.numero)        { setAlert({ type: 'warning', message: 'El número es obligatorio.' }); return; }
    if (!form.fecha_emision) { setAlert({ type: 'warning', message: 'La fecha de emisión es obligatoria.' }); return; }
    if (!form.id_area)       { setAlert({ type: 'warning', message: 'El área es obligatoria.' }); return; }
    if (detalle.length === 0 || !detalle.some(l => l.id_articulo)) {
      setAlert({ type: 'warning', message: 'Debe agregar al menos un artículo al detalle.' }); return;
    }
    const n = v => v ? parseInt(v) : null;
    const payload = {
      ...form,
      id_sucursal: n(form.id_sucursal), id_serie: n(form.id_serie),
      id_area: n(form.id_area), id_solicitante: n(form.id_solicitante),
      id_prioridad: n(form.id_prioridad), id_proveedor: n(form.id_proveedor),
      id_centro_costo: n(form.id_centro_costo),
      id_tipo_doc_ref1: n(form.id_tipo_doc_ref1), id_tipo_doc_ref2: n(form.id_tipo_doc_ref2),
      fecha_entrega: form.fecha_entrega || null,
      detalle: detalle.map((l, i) => ({
        orden: i + 1, id_articulo: n(l.id_articulo),
        cantidad: parseFloat(l.cantidad) || 1, observacion: l.observacion || null,
        id_centro_costo: n(l.id_centro_costo),
      })),
    };
    try {
      if (editingId) await requerimientoService.update(editingId, payload);
      else           await requerimientoService.create(payload);
      setToast(editingId ? 'Requerimiento actualizado' : 'Requerimiento creado');
      closeForm(); fetchRecords();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleAprobar = (r) => {
    setConfirm({
      title: 'Aprobar Requerimiento',
      message: `¿Aprobar el requerimiento "${r.numero}"?`,
      confirmLabel: 'Aprobar',
      confirmStyle: { background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' },
      onConfirm: async () => {
        setConfirm(null);
        try {
          await requerimientoService.aprobar(r.id_requerimiento, { estado: 'APROBADO' });
          setToast('Requerimiento aprobado');
          fetchRecords();
        } catch (err) {
          setAlert({ type: 'error', message: getErrorDetail(err, 'Error al aprobar.') });
        }
      },
    });
  };

  const handleDesaprobar = (r) => setDesaprobarTarget(r);

  const doDesaprobar = async (nuevoEstado, targetRow = null) => {
    const r = targetRow || desaprobarTarget;
    setDesaprobarTarget(null);
    try {
      await requerimientoService.aprobar(r.id_requerimiento, { estado: nuevoEstado });
      setToast(nuevoEstado === 'PENDIENTE' ? 'Requerimiento vuelto a Pendiente' : 'Requerimiento desaprobado');
      fetchRecords();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cambiar estado.') });
    }
  };

  const handleDuplicar = async (r) => {
    setEditingId(null);
    setDetalle([]);
    try {
      const [{ seriesData }, res] = await Promise.all([
        fetchCatalogos(),
        requerimientoService.getById(r.id_requerimiento),
      ]);
      const d = res.data;
      // Calcular siguiente número usando la misma serie
      let numero = '';
      if (d.id_serie) {
        const s = seriesData.find(x => String(x.id_serie) === String(d.id_serie));
        if (s) {
          const next = (s.ultimo_numero || 0) + 1;
          numero = String(next).padStart(s.maximo_numeros || 7, '0');
        }
      }
      setForm({
        id_sucursal:      d.id_sucursal      || sesionSucursal?.id_sucursal || '',
        tipo_documento:   d.tipo_documento   || '',
        id_serie:         d.id_serie         || '',
        numero,
        fecha_emision:    today(),
        id_area:          d.id_area          || '',
        id_solicitante:   d.id_solicitante   || '',
        id_prioridad:     d.id_prioridad     || '',
        id_proveedor:     d.id_proveedor     || '',
        observacion1:     d.observacion1     || '',
        observacion2:     d.observacion2     || '',
        id_centro_costo:  d.id_centro_costo  || '',
        fecha_entrega:    today(),
        id_tipo_doc_ref1: d.id_tipo_doc_ref1 || '',
        num_doc_ref1:     d.num_doc_ref1     || '',
        id_tipo_doc_ref2: d.id_tipo_doc_ref2 || '',
        num_doc_ref2:     d.num_doc_ref2     || '',
      });
      setDetalle((d.detalle || []).map(l => ({
        _key: Math.random(),
        id_articulo: l.id_articulo || '',
        cantidad: l.cantidad || 1,
        observacion: l.observacion || '',
        id_centro_costo: l.id_centro_costo || '',
        articulo_codigo: l.articulo_codigo || '',
        articulo_descripcion: l.articulo_descripcion || '',
        id_unidad: l.id_unidad || null,
        unidad_descripcion: l.unidad_descripcion || '',
      })));
      setView('form');
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar el requerimiento para duplicar.' });
    }
  };

  const handleAnular = (r) => {
    setConfirm({
      title: 'Anular Requerimiento',
      message: `¿Anular el requerimiento "${r.numero}"? Esta acción no se puede revertir.`,
      confirmLabel: 'Anular',
      confirmStyle: { background: 'rgba(248,113,113,0.15)', color: '#f87171', borderColor: 'rgba(248,113,113,0.3)' },
      onConfirm: async () => {
        setConfirm(null);
        try {
          await requerimientoService.aprobar(r.id_requerimiento, { estado: 'ANULADO' });
          setToast('Requerimiento anulado');
          fetchRecords();
        } catch (err) {
          setAlert({ type: 'error', message: getErrorDetail(err, 'Error al anular.') });
        }
      },
    });
  };

  const handlePrint = async (r) => {
    if (trabajadores.length === 0) await fetchCatalogos();
    setPrintTarget(r);
  };

  const doPrint = async (fms) => {
    const r = printTarget;
    setPrintTarget(null);
    try {
      const [detRes, empRes] = await Promise.allSettled([
        requerimientoService.getById(r.id_requerimiento),
        datosGeneralesService.get(),
      ]);
      const req = detRes.status === 'fulfilled' ? detRes.value.data : r;
      const emp = empRes.status === 'fulfilled' ? empRes.value.data : {};
      const det = req.detalle || [];
      const fmt = (d) => d ? d.split('T')[0] : '—';
      const estColor = { PENDIENTE: '#b45309', APROBADO: '#166534', RECHAZADO: '#991b1b' };
      const estBg    = { PENDIENTE: '#fef3c7', APROBADO: '#dcfce7', RECHAZADO: '#fee2e2' };

      const colCount = fms.length || 3;
      const signHtml = fms.map((f, i) => `
        <div class="sign-box">
          <div class="sign-space"></div>
          <div class="sign-line-top"></div>
          <div class="sign-rol">Quien ${ROLES_FIRMA[i] || ''}</div>
          <div class="sign-name">${f.nombres || '&nbsp;'}</div>
          ${f.dni ? `<div class="sign-dni">DNI: ${f.dni}</div>` : ''}
          ${f.cargo ? `<div class="sign-cargo">${f.cargo}</div>` : ''}
        </div>`).join('');

      const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="UTF-8"/>
<title>Requerimiento ${req.numero || ''}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10.5px; color: #1e293b; background: #fff; padding: 20px 28px 24px; }

  /* ── FRANJA SUPERIOR ── */
  .top-stripe { height: 5px; background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%); border-radius: 2px; margin-bottom: 16px; }

  /* ── CABECERA ── */
  .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .logo-box { width: 52px; height: 52px; background: #1a3a6b; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .logo-box svg { fill: #fff; }
  .logo-img { max-height: 56px; max-width: 130px; object-fit: contain; flex-shrink: 0; }
  .emp-name { font-size: 14px; font-weight: 700; color: #1a3a6b; line-height: 1.2; }
  .emp-ruc  { font-size: 10px; font-weight: 600; color: #475569; margin-top: 2px; }
  .emp-sub  { font-size: 9px; color: #64748b; margin-top: 2px; line-height: 1.5; }
  .doc-box { border: 2px solid #1a3a6b; border-radius: 8px; padding: 9px 18px; text-align: center; min-width: 165px; background: #f5f8ff; }
  .doc-box-label { font-size: 7.5px; font-weight: 700; color: #1a3a6b; text-transform: uppercase; letter-spacing: 1.2px; }
  .doc-box-title { font-size: 13px; font-weight: 800; color: #1a3a6b; margin: 3px 0 2px; letter-spacing: 0.5px; }
  .doc-box-num { font-size: 16px; font-weight: 700; color: #1a3a6b; }
  .doc-box-serie { font-size: 8.5px; color: #64748b; margin-top: 3px; }

  /* ── SEPARADOR ── */
  .divider { border: none; border-top: 2.5px solid #1a3a6b; margin: 12px 0 14px; }
  .divider-light { border: none; border-top: 1px solid #dce8f8; margin: 8px 0; }

  /* ── SECCIÓN ── */
  .section-header { background: #1a3a6b; color: #fff; font-size: 8px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 1.2px; padding: 4px 10px; margin: 12px 0 7px; border-radius: 3px; }

  /* ── GRIDS ── */
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-1 { margin-bottom: 5px; }
  .field { border: 1px solid #c8dcf5; border-radius: 3px; padding: 4px 7px; background: #f5f8ff; }
  .field-label { font-size: 7.5px; font-weight: 700; color: #7a9cc4; text-transform: uppercase; letter-spacing: 0.8px; }
  .field-value { font-size: 10.5px; color: #1e293b; font-weight: 500; min-height: 13px; margin-top: 1px; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 9px; font-weight: 700; }

  /* ── TABLA ── */
  table { width: 100%; border-collapse: collapse; margin-top: 2px; }
  thead tr { background: #1a3a6b; }
  th { font-size: 8.5px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px;
    padding: 6px 8px; text-align: left; border: 1px solid #1a3a6b; }
  th.center { text-align: center; }
  td { padding: 5px 8px; border: 1px solid #dce8f8; font-size: 10px; vertical-align: middle; color: #1e293b; }
  td.center { text-align: center; }
  tr:nth-child(even) td { background: #f5f8ff; }
  tr:nth-child(odd)  td { background: #fff; }
  tfoot td { background: #e8f0fb !important; font-weight: 700; font-size: 9.5px; color: #1a3a6b; border-top: 2px solid #a8c4e8; }

  /* ── FIRMAS ── */
  .signatures { display: grid; grid-template-columns: repeat(${colCount}, 1fr); gap: 0 32px; margin-top: 48px; }
  .sign-box { text-align: center; }
  .sign-space { height: 52px; }
  .sign-line-top { border-top: 1.5px solid #1a3a6b; margin: 0 8px; }
  .sign-rol   { font-size: 8px; color: #7a9cc4; text-transform: uppercase; letter-spacing: 0.7px; margin-top: 4px; }
  .sign-name  { font-size: 10px; font-weight: 700; color: #1a3a6b; margin-top: 3px; }
  .sign-dni   { font-size: 8.5px; color: #64748b; margin-top: 1px; }
  .sign-cargo { font-size: 8.5px; color: #475569; font-weight: 600; margin-top: 1px; }

  /* ── PIE ── */
  .page-footer { margin-top: 22px; padding-top: 8px; border-top: 1px solid #dce8f8;
    display: flex; justify-content: space-between; font-size: 8px; color: #94a3b8; }

  @media print {
    body { padding: 10px 16px 16px; }
    @page { margin: 8mm 10mm; size: A4; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
    .top-stripe { background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%) !important; }
    .section-header { background: #1a3a6b !important; color: #fff !important; }
    thead tr { background: #1a3a6b !important; }
    th { color: #fff !important; background: #1a3a6b !important; }
    .field { background: #f5f8ff !important; }
    tr:nth-child(even) td { background: #f5f8ff !important; }
    tfoot td { background: #e8f0fb !important; }
    .doc-box { background: #f5f8ff !important; }
    .logo-box { background: #1a3a6b !important; }
  }
</style>
</head><body>

<div class="top-stripe"></div>

<div class="header">
  <div class="header-left">
    ${emp.logo_base64
      ? `<img src="${emp.logo_base64}" class="logo-img" alt="Logo" />`
      : `<div class="logo-box"><svg viewBox="0 0 24 24" width="28" height="28"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg></div>`
    }
    <div>
      <div class="emp-name">${emp.razon_social || 'Empresa'}</div>
      <div class="emp-ruc">RUC: ${emp.ruc_empresa || '—'}</div>
      <div class="emp-sub">${emp.direccion || ''}${emp.telefono ? ' &nbsp;·&nbsp; Tel: ' + emp.telefono : ''}</div>
    </div>
  </div>
  <div class="doc-box">
    <div class="doc-box-label">Documento</div>
    <div class="doc-box-title">REQUERIMIENTO</div>
    <div class="doc-box-num">${req.numero || '—'}</div>
    ${req.serie_codigo ? `<div class="doc-box-serie">Serie: ${req.serie_codigo}</div>` : ''}
  </div>
</div>

<hr class="divider"/>

<div class="section-header">Información General</div>
<div class="grid-4">
  <div class="field"><div class="field-label">Fecha Emisión</div><div class="field-value">${fmt(req.fecha_emision)}</div></div>
  <div class="field"><div class="field-label">Fecha Entrega</div><div class="field-value">${fmt(req.fecha_entrega)}</div></div>
  <div class="field"><div class="field-label">Prioridad</div><div class="field-value">${req.prioridad_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Estado</div><div class="field-value">
    <span class="badge" style="background:${estBg[req.estado]||'#f1f5f9'};color:${estColor[req.estado]||'#475569'}">${req.estado || '—'}</span>
  </div></div>
</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Sucursal</div><div class="field-value">${req.sucursal_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Área</div><div class="field-value">${req.area_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Centro de Costo</div><div class="field-value">${req.centro_costo_descripcion || '—'}</div></div>
</div>
<div class="grid-2">
  <div class="field"><div class="field-label">Solicitante</div><div class="field-value">${req.solicitante_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Proveedor Sugerido</div><div class="field-value">${req.proveedor_nombre || '—'}</div></div>
</div>
${(req.id_tipo_doc_ref1 || req.num_doc_ref1 || req.id_tipo_doc_ref2 || req.num_doc_ref2) ? `
<div class="grid-2">
  ${req.num_doc_ref1 ? `<div class="field"><div class="field-label">Doc. Ref. 1 (${req.tipo_doc_ref1_descripcion || ''})</div><div class="field-value">${req.num_doc_ref1}</div></div>` : '<div></div>'}
  ${req.num_doc_ref2 ? `<div class="field"><div class="field-label">Doc. Ref. 2 (${req.tipo_doc_ref2_descripcion || ''})</div><div class="field-value">${req.num_doc_ref2}</div></div>` : '<div></div>'}
</div>` : ''}
${req.observacion1 ? `<div class="grid-1"><div class="field"><div class="field-label">Observaciones</div><div class="field-value">${req.observacion1}${req.observacion2 ? ' · ' + req.observacion2 : ''}</div></div></div>` : ''}

<div class="section-header">Artículos Solicitados</div>
<table>
  <thead>
    <tr>
      <th style="width:28px" class="center">#</th>
      <th style="width:88px">Código</th>
      <th>Descripción del Artículo</th>
      <th style="width:60px" class="center">Unidad</th>
      <th style="width:60px" class="center">Cantidad</th>
      <th style="width:120px">Centro de Costo</th>
      <th>Observación</th>
    </tr>
  </thead>
  <tbody>
    ${det.map((d, i) => `<tr>
      <td class="center" style="color:#94a3b8">${i + 1}</td>
      <td style="font-family:monospace;font-size:9.5px">${d.articulo_codigo || '—'}</td>
      <td style="font-weight:500">${d.articulo_descripcion || '—'}</td>
      <td class="center" style="color:#475569">${d.unidad_descripcion || '—'}</td>
      <td class="center" style="font-weight:700">${d.cantidad ?? 1}</td>
      <td style="color:#475569">${d.centro_costo_descripcion || '—'}</td>
      <td style="color:#64748b">${d.observacion || ''}</td>
    </tr>`).join('')}
    ${det.length === 0 ? '<tr><td colspan="7" class="center" style="color:#94a3b8;padding:12px">Sin artículos registrados</td></tr>' : ''}
  </tbody>
  <tfoot>
    <tr>
      <td colspan="4" style="text-align:right;padding-right:12px">Total de artículos:</td>
      <td class="center">${det.length}</td>
      <td colspan="2"></td>
    </tr>
  </tfoot>
</table>

${req.estado === 'APROBADO' && req.usuario_aprobacion ? `
<div class="section-header">Aprobación</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Aprobado por</div><div class="field-value">${req.usuario_aprobacion}</div></div>
  <div class="field"><div class="field-label">Fecha de Aprobación</div><div class="field-value">${fmt(req.fecha_aprobacion)}</div></div>
  ${req.observacion_aprobacion ? `<div class="field"><div class="field-label">Observación Aprobación</div><div class="field-value">${req.observacion_aprobacion}</div></div>` : '<div></div>'}
</div>` : ''}

${signHtml ? `<div class="signatures">${signHtml}</div>` : ''}

<div class="page-footer">
  <span>${emp.razon_social || ''} — RUC: ${emp.ruc_empresa || ''}</span>
  <span>Impreso: ${new Date().toLocaleDateString('es-PE', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
</div>

</body></html>`;

      const w = window.open('', '_blank', 'width=900,height=750');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 450);
    } catch (err) {
      setAlert({ type: 'error', message: 'Error al generar la impresión.' });
    }
  };

  const handleDelete = (r) => setConfirm({
    title: 'Eliminar Requerimiento',
    message: `¿Eliminar el requerimiento "${r.numero}"?`,
    onConfirm: async () => {
      setConfirm(null);
      try { await requerimientoService.delete(r.id_requerimiento); setToast('Requerimiento eliminado'); fetchRecords(); }
      catch (err) { setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar.') }); }
    },
  });

  // ── Modal Proveedor ──────────────────────────────────────────────
  const handleConsultarRuc = async () => {
    if (!formProv.ruc || formProv.ruc.length !== 11) {
      setAlert({ type: 'warning', message: 'Ingrese un RUC válido de 11 dígitos.' }); return;
    }
    setConsultandoRuc(true);
    try {
      const res = await entidadService.consultarRuc(formProv.ruc);
      setFormProv(p => ({ ...p, razon_social: res.data.razon_social || p.razon_social, direccion: res.data.direccion || p.direccion }));
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'RUC no encontrado.' });
    } finally { setConsultandoRuc(false); }
  };

  const handleSaveProv = async (e) => {
    e.preventDefault();
    if (!formProv.razon_social) { setAlert({ type: 'warning', message: 'Razón Social es obligatoria.' }); return; }
    setSavingProv(true);
    try {
      const data = {};
      Object.entries(formProv).forEach(([k, v]) => { data[k] = v === '' ? null : v; });
      const res = await entidadService.create(data);
      const nuevo = {
        ...res.data,
        codigo: res.data.ruc || '',
        display_prov: res.data.ruc ? `${res.data.ruc} — ${res.data.razon_social}` : (res.data.razon_social || ''),
      };
      setProveedores(prev => [...prev, nuevo]);
      setForm(p => ({ ...p, id_proveedor: String(nuevo.id_entidad) }));
      setModalProv(false);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar proveedor.') });
    } finally { setSavingProv(false); }
  };

  // ── Modal Artículo ───────────────────────────────────────────────
  const openModalArt = async (lineaKey = null) => {
    setArtLineaKey(lineaKey); setArtTab('general'); setAutoCode(true);
    try {
      const res = await articuloService.getNextCodigo();
      setFormArt({ ...emptyArt, codigo_interno: res.data.codigo });
    } catch { setFormArt(emptyArt); }
    setModalArt(true);
  };

  const toggleAutoCode = async () => {
    if (!autoCode) {
      try { const r = await articuloService.getNextCodigo(); setFormArt(p => ({ ...p, codigo_interno: r.data.codigo })); }
      catch {}
    } else { setFormArt(p => ({ ...p, codigo_interno: '' })); }
    setAutoCode(!autoCode);
  };

  const refreshArtCombo = async (type) => {
    try {
      const svcMap = { unidades: [unidadMedidaService.getAll, setUnidades], marcas: [() => tablaService('marcas').getAll(), setMarcas], familias: [() => tablaService('familias').getAll(), setFamilias], grupos: [() => tablaService('grupos').getAll(), setGrupos], lineas: [() => tablaService('lineas').getAll(), setLineas], categorias: [() => tablaService('categorias').getAll(), setCategorias], 'tipos-existencia': [() => tablaService('tipos-existencia').getAll(), setTiposExistencia], 'cuentas-contables': [() => tablaService('cuentas-contables').getAll(), setCuentasContables], modelos: [() => tablaService('modelos').getAll(), setModelos] };
      if (svcMap[type]) { const [fn, setter] = svcMap[type]; const r = await fn(); setter(r.data); }
    } catch {}
  };

  const openMiniModal = (type, label) => {
    setMiniModal({ type, label });
    setMiniForm({ codigo: '', descripcion: '', codigo_sunat: '' });
  };

  const handleMiniSubmit = async (e) => {
    e.preventDefault();
    if (!miniForm.codigo || !miniForm.descripcion) {
      setAlert({ type: 'warning', message: 'Código y descripción son obligatorios.' }); return;
    }
    try {
      let res;
      if (miniModal.type === 'unidades') {
        res = await unidadMedidaService.create({ codigo: miniForm.codigo, codigo_sunat: miniForm.codigo_sunat || '', descripcion: miniForm.descripcion });
        await refreshArtCombo('unidades');
        if (res.data?.id_unidad) setFormArt(p => ({ ...p, id_unidad: res.data.id_unidad }));
      } else {
        res = await tablaService(miniModal.type).create({ codigo: miniForm.codigo, descripcion: miniForm.descripcion });
        await refreshArtCombo(miniModal.type);
        const idMap = { marcas: 'id_marca', familias: 'id_familia', grupos: 'id_grupo', lineas: 'id_linea', categorias: 'id_categoria', 'tipos-existencia': 'id_tipo_existencia', 'cuentas-contables': 'id_cuenta_contable', modelos: 'id_modelo' };
        const field = idMap[miniModal.type];
        if (field && res.data[field]) setFormArt(p => ({ ...p, [field]: res.data[field] }));
      }
      setMiniModal(null);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al crear.') });
    }
  };

  const handleSaveArt = async (e) => {
    e.preventDefault();

    const generalRequired = ['codigo_interno', 'descripcion', 'id_tipo_existencia', 'id_cuenta_contable'];
    const clasifRequired = ['id_unidad', 'id_marca', 'id_modelo', 'id_familia', 'id_grupo', 'id_linea', 'id_categoria'];
    const missingGeneral = generalRequired.some(k => !formArt[k]);
    const missingClasif = clasifRequired.some(k => !formArt[k]);
    if (missingGeneral || missingClasif) {
      setArtTab(missingGeneral ? 'general' : 'clasificacion');
      setAlert({ type: 'warning', message: 'Complete los campos obligatorios marcados en rojo.' });
      return;
    }

    setSavingArt(true);
    try {
      const data = {};
      Object.entries(formArt).forEach(([k, v]) => {
        if (v === '' || v === null) data[k] = null;
        else if (['id_unidad','id_marca','id_modelo','id_familia','id_grupo','id_linea','id_categoria','id_tipo_existencia','id_cuenta_contable'].includes(k)) data[k] = parseInt(v) || null;
        else if (['peso','ancho','largo','grosor','volumen','area','stock_minimo','stock_maximo'].includes(k)) data[k] = parseFloat(v) || null;
        else data[k] = v;
      });
      const res = await articuloService.create(data);
      const umNombre = unidades.find(u => String(u.id_unidad) === String(res.data.id_unidad))?.descripcion || '';
      const codNew = (res.data.codigo_interno || '').trim();
      let descNew = (res.data.descripcion || '').trim();
      if (codNew && descNew.toUpperCase().startsWith(codNew.toUpperCase())) {
        descNew = descNew.slice(codNew.length).replace(/^[\s\-–—·:]+/, '');
      }
      const nuevo = {
        ...res.data,
        codigo: codNew,
        display_linea: [codNew, descNew, umNombre].filter(Boolean).join(' · '),
      };
      setArticulos(prev => [...prev, nuevo]);
      if (artLineaKey !== null) updateLinea(artLineaKey, 'id_articulo', String(nuevo.id_articulo));
      setModalArt(false);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al crear artículo.') });
    } finally { setSavingArt(false); }
  };

  // ── Modal Centro de Costo ────────────────────────────────────────
  const handleSaveCC = async (e) => {
    e.preventDefault();
    if (!formCC.codigo || !formCC.descripcion) { setAlert({ type: 'warning', message: 'Código y descripción son obligatorios.' }); return; }
    setSavingCC(true);
    try {
      const res = await tablaService('centros-costo').create(formCC);
      const nuevoCC = { ...res.data, display_cc: res.data.descripcion };
      setCentrosCosto(prev => [...prev, nuevoCC]);
      setForm(p => ({ ...p, id_centro_costo: String(res.data.id_centro_costo) }));
      setModalCC(false); setFormCC({ codigo: '', descripcion: '' });
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al crear centro de costo.') });
    } finally { setSavingCC(false); }
  };

  // ── Datos computados ─────────────────────────────────────────────
  const seriesFiltradas = series
    .filter(s => s.codigo === form.tipo_documento)
    .map(s => ({ ...s, codigo: '', descripcion_display: `${s.serie} — ${s.descripcion}` }));

  // Para autocomplete en panel de filtros — sin campo 'codigo' para evitar prefijo duplicado
  const seriesParaFiltro = useMemo(() =>
    series.map(s => ({ ...s, codigo: '', display_serie: `${s.serie} — ${s.descripcion}` })),
    [series]
  );

  // ── Lookup rápido de artículos y unidades ────────────────────────
  const articuloMap = useMemo(() =>
    Object.fromEntries(articulos.map(a => [String(a.id_articulo), a])),
    [articulos]
  );
  const unidadMap = useMemo(() =>
    Object.fromEntries(unidades.map(u => [String(u.id_unidad), u.descripcion])),
    [unidades]
  );

  // ── Registros filtrados ──────────────────────────────────────────
  const filteredRecords = useMemo(() => {
    let r = records;
    if (filters.serie)       r = r.filter(x => String(x.id_serie) === filters.serie);
    if (filters.numero) {
      const q = filters.numero.toLowerCase();
      r = r.filter(x => {
        // Comparar solo contra la parte numérica, sin el prefijo de serie
        const soloNumero = x.serie_codigo
          ? (x.numero || '').replace(x.serie_codigo + '-', '')
          : (x.numero || '');
        return soloNumero.toLowerCase().includes(q);
      });
    }
    if (filters.fechaInicio) r = r.filter(x => x.fecha_emision && x.fecha_emision.split('T')[0] >= filters.fechaInicio);
    if (filters.fechaFin)    r = r.filter(x => x.fecha_emision && x.fecha_emision.split('T')[0] <= filters.fechaFin);
    if (filters.estado)      r = r.filter(x => x.estado === filters.estado);
    if (filters.idArea)      r = r.filter(x => String(x.id_area) === filters.idArea);
    if (filters.idSucursal)  r = r.filter(x => String(x.id_sucursal) === filters.idSucursal);
    if (filters.idArticulo && filters.codigoArticulo) {
      r = r.filter(x => (x.codigos_articulos || '').split('|').includes(filters.codigoArticulo));
    }
    if (filters.idCentroCosto && filters.codigoCentroCosto) {
      r = r.filter(x => (x.codigos_centros_costo || '').split('|').includes(filters.codigoCentroCosto));
    }
    return r;
  }, [records, filters]);

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;
  const clearFilters = () => setFilters({ serie: '', numero: '', fechaInicio: weekAgo(), fechaFin: today(), estado: '', idArea: '', idSucursal: '', idArticulo: '', codigoArticulo: '', articuloDescripcion: '', idCentroCosto: '', codigoCentroCosto: '' });

  // Cerrar menú importar al clic fuera
  useEffect(() => {
    if (!showImportMenu) return;
    const handler = (e) => {
      if (!importMenuRef.current?.contains(e.target)) setShowImportMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showImportMenu]);

  // ── Modal Ver RQ: carga detalle + atención OC en paralelo ─────────
  const abrirViewRQ = async (r) => {
    setViewModal(r);
    setViewData(null);
    setLoadingView(true);
    try {
      const [detRes, atenRes] = await Promise.allSettled([
        requerimientoService.getById(r.id_requerimiento),
        ordenCompraService.getAtencionRq(r.id_requerimiento),
      ]);
      const req     = detRes.status  === 'fulfilled' ? detRes.value.data  : r;
      const atencion = atenRes.status === 'fulfilled' ? atenRes.value.data : { items: [], ocs: [] };
      setViewData({ req, atencion });
    } catch { setAlert({ type: 'error', message: 'Error al cargar el requerimiento.' }); setViewModal(null); }
    finally { setLoadingView(false); }
  };

  // ── Preview OC en nueva pestaña (sin auto-imprimir) ──────────────
  const previewOC = async (id_orden) => {
    try {
      const [detRes, empRes] = await Promise.allSettled([
        ordenCompraService.getById(id_orden),
        datosGeneralesService.get(),
      ]);
      const oc  = detRes.status === 'fulfilled' ? detRes.value.data : {};
      const emp = empRes.status === 'fulfilled' ? empRes.value.data : {};
      const det = oc.detalle || [];
      const fmt   = (d) => d ? d.split('T')[0] : '—';
      const fmtN  = (v, dec = 2) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
      const esPEN = oc.moneda === 'PEN';
      const sym   = esPEN ? 'S/' : 'US$';
      const ECOL  = { PENDIENTE: '#b45309', APROBADO: '#166534', DESAPROBADO: '#c2410c', ANULADO: '#475569' };
      const EBG   = { PENDIENTE: '#fef3c7', APROBADO: '#dcfce7', DESAPROBADO: '#ffedd5', ANULADO: '#f1f5f9' };
      const baseImp = esPEN ? oc.base_imponible_soles    : oc.base_imponible_dolares;
      const igvT    = esPEN ? oc.igv_soles               : oc.igv_dolares;
      const noGrav  = esPEN ? oc.no_gravado_soles        : oc.no_gravado_dolares;
      const desc    = esPEN ? oc.descuento_soles         : oc.descuento_dolares;
      const total   = esPEN ? oc.total_soles             : oc.total_dolares;
      // Centro de costo: cabecera única o lista + columna por línea
      const ccLabelPorLinea = (d) => d.centro_costo_codigo
        ? `${d.centro_costo_codigo}${d.centro_costo_descripcion ? ' — ' + d.centro_costo_descripcion : ''}`
        : '—';
      const ccUnicos = (() => {
        const mapa = new Map();
        det.forEach(d => {
          const key = d.id_centro_costo || null;
          if (key && !mapa.has(key)) mapa.set(key, ccLabelPorLinea(d));
        });
        return mapa;
      })();
      const ccTodosIguales = det.length > 0 && ccUnicos.size === 1 && det.every(d => d.id_centro_costo);
      const ccCabeceraLabel = ccTodosIguales
        ? [...ccUnicos.values()][0]
        : [...ccUnicos.values()].join(', ');
      const mostrarColumnaCC = !ccTodosIguales && ccUnicos.size > 0;
      const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="UTF-8"/><title>Orden de Compra ${oc.numero || ''}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10.5px; color: #1e293b; background: #fff; padding: 20px 28px 24px; }
  .top-stripe { height: 5px; background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%); border-radius: 2px; margin-bottom: 16px; }
  .header { display: flex; justify-content: space-between; align-items: center; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .logo-box { width: 52px; height: 52px; background: #1a3a6b; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
  .logo-box svg { fill: #fff; }
  .logo-img { max-height: 56px; max-width: 130px; object-fit: contain; }
  .emp-name { font-size: 14px; font-weight: 700; color: #1a3a6b; }
  .emp-ruc  { font-size: 10px; font-weight: 600; color: #475569; margin-top: 2px; }
  .emp-sub  { font-size: 9px; color: #64748b; margin-top: 2px; line-height: 1.5; }
  .doc-box { border: 2px solid #1a3a6b; border-radius: 8px; padding: 9px 18px; text-align: center; min-width: 180px; background: #f5f8ff; }
  .doc-box-label { font-size: 7.5px; font-weight: 700; color: #1a3a6b; text-transform: uppercase; letter-spacing: 1.2px; }
  .doc-box-title { font-size: 13px; font-weight: 800; color: #1a3a6b; margin: 3px 0 2px; }
  .doc-box-num { font-size: 16px; font-weight: 700; color: #1a3a6b; }
  .divider { border: none; border-top: 2.5px solid #1a3a6b; margin: 10px 0 8px; }
  .section-header { background: #1a3a6b; color: #fff; font-size: 7.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 3px 8px; margin: 6px 0 3px; border-radius: 3px; }
  .grid-6 { display: grid; grid-template-columns: repeat(6, 1fr); gap: 3px 6px; margin-bottom: 3px; }
  .grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 3px 6px; margin-bottom: 3px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3px 6px; margin-bottom: 3px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 6px; margin-bottom: 3px; }
  .grid-1 { margin-bottom: 3px; }
  .span-2 { grid-column: span 2; }
  .span-3 { grid-column: span 3; }
  .field { border: 1px solid #c8dcf5; border-radius: 3px; padding: 2px 6px; background: transparent; }
  .field-label { font-size: 6.8px; font-weight: 700; color: #7a9cc4; text-transform: uppercase; letter-spacing: 0.6px; line-height: 1.2; }
  .field-value { font-size: 10px; color: #1e293b; font-weight: 500; min-height: 12px; line-height: 1.25; margin-top: 0; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 9px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin-top: 2px; }
  thead tr { background: #1a3a6b; }
  th { font-size: 8px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; padding: 5px 7px; text-align: left; border: 1px solid #1a3a6b; }
  th.right { text-align: right; } th.center { text-align: center; }
  td { padding: 4px 7px; border: 1px solid #dce8f8; font-size: 9.5px; vertical-align: middle; color: #1e293b; }
  td.right { text-align: right; } td.center { text-align: center; }
  tr:nth-child(even) td { background: #f5f8ff; }
  tr:nth-child(odd)  td { background: #fff; }
  .totals-table { width: auto; margin-left: auto; min-width: 280px; margin-top: 8px; border-collapse: collapse; }
  .totals-table td { font-size: 10px; padding: 4px 10px; border: 1px solid #dce8f8; }
  .totals-table td:first-child { text-align: right; color: #475569; font-weight: 500; background: #f5f8ff; }
  .totals-table td:last-child  { text-align: right; font-weight: 600; color: #1e293b; min-width: 110px; }
  .totals-table tr.total-main td { font-size: 12px; font-weight: 800; color: #1a3a6b; background: #e8f0fb !important; border-top: 2px solid #a8c4e8; }
  .page-footer { margin-top: 22px; padding-top: 8px; border-top: 1px solid #dce8f8; display: flex; justify-content: space-between; font-size: 8px; color: #94a3b8; }
  @media print {
    body { padding: 10px 16px 16px; }
    @page { margin: 8mm 10mm; size: A4; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .top-stripe { background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%) !important; }
    .section-header { background: #1a3a6b !important; color: #fff !important; }
    thead tr { background: #1a3a6b !important; }
    th { color: #fff !important; background: #1a3a6b !important; }
    .field { background: transparent !important; }
    tr:nth-child(even) td { background: #f5f8ff !important; }
    .totals-table tr.total-main td { background: #e8f0fb !important; }
    .totals-table td:first-child { background: #f5f8ff !important; }
    .doc-box { background: #f5f8ff !important; }
    .logo-box { background: #1a3a6b !important; }
  }
</style></head><body>
<div class="top-stripe"></div>
<div class="header">
  <div class="header-left">
    ${emp.logo_base64
      ? `<img src="${emp.logo_base64}" class="logo-img" alt="Logo" />`
      : `<div class="logo-box"><svg viewBox="0 0 24 24" width="28" height="28"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg></div>`}
    <div>
      <div class="emp-name">${emp.razon_social || 'Empresa'}</div>
      <div class="emp-ruc">RUC: ${emp.ruc_empresa || '—'}</div>
      <div class="emp-sub">${emp.direccion || ''}${emp.telefono ? ' · Tel: ' + emp.telefono : ''}</div>
    </div>
  </div>
  <div class="doc-box">
    <div class="doc-box-label">Documento</div>
    <div class="doc-box-title">ORDEN DE COMPRA</div>
    <div class="doc-box-num">${oc.serie_codigo ? oc.serie_codigo.trim() + '-' : ''}${oc.numero || '—'}</div>
  </div>
</div>
<hr class="divider"/>
<div class="section-header">Información General</div>
<div class="grid-6">
  <div class="field"><div class="field-label">Fecha Emisión</div><div class="field-value">${fmt(oc.fecha_emision)}</div></div>
  <div class="field"><div class="field-label">Fecha Entrega</div><div class="field-value">${fmt(oc.fecha_entrega)}</div></div>
  <div class="field"><div class="field-label">Sucursal</div><div class="field-value">${oc.sucursal_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Moneda</div><div class="field-value">${oc.moneda === 'USD' ? 'USD' : 'PEN'}${oc.moneda === 'USD' && parseFloat(oc.tipo_cambio) ? ` · TC ${fmtN(oc.tipo_cambio, 4)}` : ''}</div></div>
  <div class="field"><div class="field-label">Tipo OC</div><div class="field-value">${oc.tipo_oc || '—'}</div></div>
  <div class="field"><div class="field-label">Estado</div><div class="field-value">
    <span class="badge" style="background:${EBG[oc.estado]||'#f1f5f9'};color:${ECOL[oc.estado]||'#475569'}">${oc.estado || '—'}</span>
  </div></div>
</div>
<div class="section-header">Proveedor</div>
<div class="grid-4">
  <div class="field span-2"><div class="field-label">Razón Social</div><div class="field-value">${oc.proveedor_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">RUC</div><div class="field-value">${oc.proveedor_ruc || '—'}</div></div>
  <div class="field"><div class="field-label">Contacto</div><div class="field-value">${oc.proveedor_contacto || '—'}</div></div>
</div>
<div class="grid-4">
  <div class="field span-2"><div class="field-label">Dirección</div><div class="field-value">${oc.proveedor_direccion || '—'}</div></div>
  <div class="field"><div class="field-label">Teléfono</div><div class="field-value">${oc.proveedor_telefono || oc.proveedor_celular || '—'}</div></div>
  <div class="field"><div class="field-label">Correo</div><div class="field-value">${oc.proveedor_correo || '—'}</div></div>
</div>
<div class="section-header">Entrega y Pago</div>
<div class="grid-4">
  <div class="field"><div class="field-label">Forma de Pago</div><div class="field-value">${oc.forma_pago_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Lugar de Entrega</div><div class="field-value">${oc.almacen_entrega_nombre || '—'}</div></div>
  <div class="field span-2"><div class="field-label">Dirección de Entrega</div><div class="field-value">${oc.almacen_entrega_direccion || '—'}</div></div>
</div>
${ccCabeceraLabel ? `<div class="grid-1"><div class="field"><div class="field-label">Centro${ccTodosIguales ? '' : 's'} de Costo</div><div class="field-value">${ccCabeceraLabel}</div></div></div>` : ''}
<div class="section-header">Detalle de Artículos</div>
<table>
  <thead><tr>
    <th style="width:24px" class="center">#</th>
    <th style="width:80px">Código</th>
    <th>Descripción</th>
    ${mostrarColumnaCC ? '<th style="width:70px">C. Costo</th>' : ''}
    <th style="width:50px" class="center">Cant.</th>
    <th style="width:82px" class="right">Precio</th>
    <th style="width:36px" class="center">%IGV</th>
    <th style="width:92px" class="right">Total Línea</th>
  </tr></thead>
  <tbody>
    ${det.map((d, i) => {
      const precio = esPEN ? d.precio_soles : d.precio_dolares;
      const totalL = esPEN ? d.total_con_descuento_soles : d.total_con_descuento_dolares;
      return `<tr>
        <td class="center" style="color:#94a3b8">${i + 1}</td>
        <td style="font-family:monospace;font-size:9px">${d.articulo_codigo || '—'}</td>
        <td style="font-weight:500">${d.articulo_descripcion || '—'}</td>
        ${mostrarColumnaCC ? `<td style="font-size:9px;color:#475569;font-family:monospace">${d.centro_costo_codigo || '—'}</td>` : ''}
        <td class="center" style="font-weight:700">${(parseFloat(d.cantidad)||0).toLocaleString('es-PE',{maximumFractionDigits:4})}</td>
        <td class="right">${sym} ${fmtN(precio,4)}</td>
        <td class="center">${d.porcentaje_igv ?? 18}%</td>
        <td class="right" style="font-weight:700">${sym} ${fmtN(totalL,4)}</td>
      </tr>`;
    }).join('')}
    ${det.length === 0 ? `<tr><td colspan="${mostrarColumnaCC ? 8 : 7}" class="center" style="color:#94a3b8;padding:12px">Sin artículos</td></tr>` : ''}
  </tbody>
</table>
<table class="totals-table">
  <tbody>
    ${parseFloat(baseImp) > 0 ? `<tr><td>Base Imponible:</td><td>${sym} ${fmtN(baseImp,4)}</td></tr>` : ''}
    ${parseFloat(igvT)   > 0 ? `<tr><td>IGV:</td><td>${sym} ${fmtN(igvT,4)}</td></tr>` : ''}
    ${parseFloat(noGrav) > 0 ? `<tr><td>No Gravados:</td><td>${sym} ${fmtN(noGrav,4)}</td></tr>` : ''}
    ${parseFloat(desc)   > 0 ? `<tr><td>Descuentos:</td><td>− ${sym} ${fmtN(desc,4)}</td></tr>` : ''}
    <tr class="total-main"><td>TOTAL A PAGAR:</td><td>${sym} ${fmtN(total,2)}</td></tr>
    ${!esPEN ? `<tr><td style="color:#64748b">Equivalente en soles:</td><td style="color:#64748b">S/ ${fmtN(oc.total_soles,2)}</td></tr>` : ''}
  </tbody>
</table>
${oc.usuario_aprobacion || oc.estado === 'APROBADO' ? `
<div class="section-header">Aprobación</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Aprobado por</div><div class="field-value">${oc.usuario_aprobacion || '—'}</div></div>
  <div class="field"><div class="field-label">Fecha</div><div class="field-value">${oc.fecha_aprobacion ? new Date(oc.fecha_aprobacion).toLocaleString('es-PE',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</div></div>
  ${oc.observacion_aprobacion ? `<div class="field"><div class="field-label">Observación</div><div class="field-value">${oc.observacion_aprobacion}</div></div>` : '<div></div>'}
</div>` : ''}
<div class="page-footer">
  <span>${emp.razon_social || ''} — RUC: ${emp.ruc_empresa || ''}</span>
  <span>Impreso: ${new Date().toLocaleDateString('es-PE',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
</div>
</body></html>`;
      const w = window.open('', '_blank', 'width=950,height=780');
      w.document.write(html);
      w.document.close();
      w.focus();
    } catch { setAlert({ type: 'error', message: 'Error al generar vista previa de la OC.' }); }
  };

  // ── Modal Atención OC ─────────────────────────────────────────────
  const abrirAtencionOC = async (rq) => {
    setAtencionOCModal(rq);
    setAtencionOCData(null);
    setLoadingAtencionOC(true);
    try {
      const res = await ordenCompraService.getAtencionRq(rq.id_requerimiento);
      setAtencionOCData(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar datos de atención OC.' });
      setAtencionOCModal(null);
    } finally { setLoadingAtencionOC(false); }
  };

  // ── Columnas de la lista ─────────────────────────────────────────
  const columns = [
    { key: 'numero', label: 'Número', width: 150, style: { fontWeight: 700 },
      render: r => r.serie_codigo ? `${r.serie_codigo.trim()}-${r.numero}` : r.numero },
    { key: 'fecha_emision',       label: 'Fecha',       width: 88, render: r => r.fecha_emision?.split('T')[0] || '--' },
    { key: 'tipo_documento',      label: 'Tipo',        width: 48 },
    { key: 'sucursal_nombre',     label: 'Sucursal',    width: 110 },
    { key: 'area_descripcion',    label: 'Área',        width: 100 },
    { key: 'solicitante_nombre',  label: 'Solicitante' },
    { key: 'prioridad_descripcion', label: 'Prioridad', width: 80 },
    { key: 'estado', label: 'Estado', width: 95,
      render: r => {
        const e = ESTADO_COLORS[r.estado] || { color: '#94a3b8', bg: 'transparent' };
        return (
          <span style={{ color: e.color, background: e.bg, padding: '2px 8px', borderRadius: 20,
            fontSize: 10.5, fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' }}>
            {r.estado}
          </span>
        );
      }},
    { key: 'fecha_entrega', label: 'F. Entrega', width: 88, render: r => r.fecha_entrega?.split('T')[0] || '--' },
    { key: '_seguimiento_rq', label: 'Seguimiento RQ', width: 110,
      render: r => {
        const est = r.seguimiento_rq || 'EN PROCESO';
        const cfg = est === 'COMPLETADO'
          ? { label: 'Completado', color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)' }
          : { label: 'En proceso', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' };
        return (
          <span style={{
            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
            borderRadius: 10, padding: '3px 9px', fontSize: 10.5, fontWeight: 700,
            display: 'inline-block', whiteSpace: 'nowrap',
          }}>{cfg.label}</span>
        );
      }},
    { key: '_acciones', label: 'Acciones', width: 240,
      render: r => (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap' }}>
          {/* Ver */}
          <button className="rq-btn-icon rq-btn-view" title="Ver detalle" onClick={() => abrirViewRQ(r)}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
          </button>
          {/* Imprimir */}
          <button className="rq-btn-icon rq-btn-print" title="Imprimir PDF" onClick={() => handlePrint(r)}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9v-1h8v1H6zm8 2H6v-1h8v1z" clipRule="evenodd"/></svg>
          </button>
          {/* Seguimiento */}
          <button className="rq-btn-icon rq-btn-view" title="Seguimiento" onClick={() => abrirSeguimiento(r)}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="12" height="12"><path d="M3 10h3l3-6 4 12 3-6h3" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          {/* Editar — solo en PENDIENTE o DESAPROBADO y sin documentos derivados */}
          {(r.estado === 'PENDIENTE' || r.estado === 'DESAPROBADO') && (
            <button
              className="rq-btn-icon rq-btn-edit"
              title={r.bloqueado ? 'No editable: tiene documentos derivados' : 'Editar'}
              onClick={() => openEdit(r)}
              disabled={!!r.bloqueado}
              style={r.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
            </button>
          )}
          {/* Duplicar — siempre disponible */}
          <button className="rq-btn-icon rq-btn-duplicar" title="Duplicar requerimiento" onClick={() => handleDuplicar(r)}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z"/><path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z"/></svg>
          </button>

          <div className="rq-btn-sep" />

          {/* Acciones de estado */}
          {r.estado === 'PENDIENTE' && (<>
            <button className="rq-btn-aprobar" onClick={() => handleAprobar(r)}>
              <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>
              Aprobar
            </button>
            <button
              className="rq-btn-anular"
              onClick={() => handleAnular(r)}
              disabled={!!r.bloqueado}
              title={r.bloqueado ? 'No anulable: tiene documentos derivados' : 'Anular'}
              style={r.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/></svg>
              Anular
            </button>
          </>)}
          {r.estado === 'APROBADO' && (
            <button
              className="rq-btn-desaprobar"
              onClick={() => handleDesaprobar(r)}
              disabled={!!r.bloqueado}
              title={r.bloqueado ? 'No desaprobable: tiene documentos derivados' : 'Desaprobar'}
              style={r.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10"><path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854z"/></svg>
              Desaprobar
            </button>
          )}
          {r.estado === 'DESAPROBADO' && (
            <button className="rq-btn-volver" onClick={() => doDesaprobar('PENDIENTE', r)}>
              ↩ Pendiente
            </button>
          )}
        </div>
      )},
  ];

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Layout title="Requerimientos" subtitle={view === 'form' ? (editingId ? 'Editar' : 'Nuevo') : 'Listado'}>

      {view === 'list' ? (
        <div className="rq-list">
          {/* ── Encabezado con acciones ── */}
          <div className="page-header">
            <h2>Requerimientos</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>

              {/* Filtros toggle */}
              <button className="btn btn-secondary" onClick={() => setShowFilters(v => !v)}
                style={showFilters ? { borderColor: '#3b82f6', color: '#3b82f6' } : {}}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" style={{ marginRight: 5 }}>
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L13 10.414V17a1 1 0 01-.553.894l-4 2A1 1 0 017 19v-8.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd"/>
                </svg>
                Filtros
                {activeFilterCount > 0 && (
                  <span className="rq-filter-badge">{activeFilterCount}</span>
                )}
              </button>

              {/* Exportar Excel */}
              <button className="btn btn-secondary" onClick={() => exportRef.current?.()} title="Exportar a Excel">
                <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginRight: 5 }} fill="none">
                  <rect x="2" y="2" width="20" height="20" rx="2" fill="#217346"/>
                  <path d="M7 7h10v10H7z" fill="#217346"/>
                  <path d="M13.5 8.5L11 12l2.5 3.5H12l-1.5-2.1L9 15.5H7.5L10 12 7.5 8.5H9l1.5 2.1 1.5-2.1h1.5z" fill="white"/>
                  <rect x="13" y="8.5" width="3.5" height="1.2" rx="0.3" fill="white"/>
                  <rect x="13" y="11.4" width="3.5" height="1.2" rx="0.3" fill="white"/>
                  <rect x="13" y="14.3" width="3.5" height="1.2" rx="0.3" fill="white"/>
                </svg>
                Exportar
              </button>

              {/* Importar (con submenu) */}
              <div style={{ position: 'relative' }} ref={importMenuRef}>
                <button className="btn btn-secondary" onClick={() => setShowImportMenu(v => !v)}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" style={{ marginRight: 5 }}>
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                  Importar
                  <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11" style={{ marginLeft: 4 }}>
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>
                {showImportMenu && (
                  <div className="rq-import-menu">
                    <button onClick={() => { importRef.current?.(); setShowImportMenu(false); }}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
                      </svg>
                      Importar desde Excel
                    </button>
                    <button onClick={() => { templateRef.current?.(); setShowImportMenu(false); }}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586L7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd"/>
                      </svg>
                      Descargar Plantilla
                    </button>
                  </div>
                )}
              </div>

              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkDelete(true)}
                disabled={filteredRecords.length === 0}
                title={filteredRecords.length === 0 ? 'No hay registros para eliminar' : 'Eliminar todos los registros visibles'}
                style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5 }}>
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Eliminar todos
              </button>

              <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
            </div>
          </div>

          {/* ── Panel de filtros colapsable ── */}
          {showFilters && (
            <div className="rq-filter-panel">
              <div className="rq-filter-grid">

                {/* Serie */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Serie</label>
                  <AutoComplete
                    value={filters.serie}
                    options={seriesParaFiltro}
                    idField="id_serie"
                    displayField="display_serie"
                    selectedField="serie"
                    hideCode
                    onChange={v => setFilters(p => ({ ...p, serie: v ? String(v) : '' }))}
                    placeholder="Todas las series..."
                  />
                </div>

                {/* Número */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Número</label>
                  <div className="rq-filter-input-wrap">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" className="rq-filter-input-icon">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
                    </svg>
                    <input className="rq-filter-input" value={filters.numero}
                      onChange={e => setFilters(p => ({ ...p, numero: e.target.value }))}
                      placeholder="Buscar número..." />
                    {filters.numero && <button className="rq-filter-input-clear" onClick={() => setFilters(p => ({ ...p, numero: '' }))}>×</button>}
                  </div>
                </div>

                {/* Artículo (campo ancho — ocupa 2 columnas) */}
                <div className="rq-filter-field rq-filter-field--wide">
                  <label className="rq-filter-label">Artículo</label>
                  <AutoComplete
                    value={filters.idArticulo}
                    idField="id_articulo"
                    displayField={(a) => [a.codigo_interno, a.descripcion].filter(Boolean).join(' — ')}
                    asyncSearch={async (q) => { const r = await articuloService.search(q); return r.data || []; }}
                    minChars={3}
                    initialSelected={filters.idArticulo ? {
                      id_articulo: filters.idArticulo,
                      codigo_interno: filters.codigoArticulo,
                      descripcion: filters.articuloDescripcion,
                    } : null}
                    onChange={(v, item) => setFilters(p => ({
                      ...p,
                      idArticulo: v ? String(v) : '',
                      codigoArticulo: item?.codigo_interno || '',
                      articuloDescripcion: item?.descripcion || '',
                    }))}
                    placeholder="Buscar por código o descripción (mín. 3)..."
                  />
                </div>

                {/* Fecha desde */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Desde</label>
                  <input type="date" className="rq-filter-date" value={filters.fechaInicio}
                    onChange={e => setFilters(p => ({ ...p, fechaInicio: e.target.value }))} />
                </div>

                {/* Fecha hasta */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Hasta</label>
                  <input type="date" className="rq-filter-date" value={filters.fechaFin}
                    onChange={e => setFilters(p => ({ ...p, fechaFin: e.target.value }))} />
                </div>

                {/* Estado */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Estado</label>
                  <select className="rq-filter-select" value={filters.estado}
                    onChange={e => setFilters(p => ({ ...p, estado: e.target.value }))}>
                    <option value="">Todos</option>
                    {Object.keys(ESTADO_COLORS).map(k => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                </div>

                {/* Área */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Área</label>
                  <AutoComplete
                    value={filters.idArea}
                    options={areas}
                    idField="id_area"
                    displayField="descripcion"
                    hideCode
                    onChange={v => setFilters(p => ({ ...p, idArea: v ? String(v) : '' }))}
                    placeholder="Todas las áreas..."
                  />
                </div>

                {/* Sucursal */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Sucursal</label>
                  <AutoComplete
                    value={filters.idSucursal}
                    options={sucursales}
                    idField="id_sucursal"
                    displayField="nombre"
                    hideCode
                    onChange={v => setFilters(p => ({ ...p, idSucursal: v ? String(v) : '' }))}
                    placeholder="Todas las sucursales..."
                  />
                </div>

                {/* Centro de Costo */}
                <div className="rq-filter-field">
                  <label className="rq-filter-label">Centro de Costo</label>
                  <AutoComplete
                    value={filters.idCentroCosto}
                    options={centrosCosto}
                    idField="id_centro_costo"
                    displayField="display_cc"
                    codeField="codigo"
                    onChange={(v, item) => setFilters(p => ({
                      ...p,
                      idCentroCosto: v ? String(v) : '',
                      codigoCentroCosto: item?.codigo || '',
                    }))}
                    placeholder="Todos los centros de costo..."
                  />
                </div>

              </div>

              {/* Acciones del panel */}
              <div className="rq-filter-actions">
                <span className="rq-filter-results">
                  <strong>{filteredRecords.length}</strong> resultado{filteredRecords.length !== 1 ? 's' : ''}
                </span>
                {activeFilterCount > 0 && (
                  <button className="rq-filter-clear-btn" onClick={clearFilters}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                    </svg>
                    Limpiar todos los filtros
                  </button>
                )}
              </div>
            </div>
          )}

          <DataTable columns={columns} data={filteredRecords} idField="id_requerimiento"
            loading={loading} emptyMessage="No hay requerimientos registrados"
            exportFileName="requerimientos"
            hideSearch hideToolbarRight
            importColumns={RQ_IMPORT_COLUMNS}
            importRef={importRef} templateRef={templateRef} exportRef={exportRef} />
        </div>
      ) : (
        /* ═══════════════════════════════════════════════════════════
           VISTA FORMULARIO — PÁGINA COMPLETA
        ═══════════════════════════════════════════════════════════ */
        <form className="rq-page" onSubmit={handleSubmit}>

          {/* ── Header ── */}
          <div className="rq-header">
            <div className="rq-header-left">
              <button type="button" className="rq-back-btn" onClick={closeForm}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Volver
              </button>
              <div className="rq-header-title">
                <span className="rq-header-meta">Logística · Requerimientos</span>
                <span className="rq-header-name">{editingId ? 'Editar Requerimiento' : 'Nuevo Requerimiento'}</span>
              </div>
            </div>
            <div className="rq-header-actions">
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={() => handlePrint(records.find(r => r.id_requerimiento === editingId) || { id_requerimiento: editingId })}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" style={{ marginRight: 5 }}><path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9v-1h8v1H6zm8 2H6v-1h8v1z" clipRule="evenodd"/></svg>
                  Imprimir
                </button>
              )}
            </div>
          </div>

          {/* ── Cuerpo ── */}
          <div className="rq-body">

            {/* ── Sección 1: Identificación del documento ── */}
            <div className="rq-card">
              <div className="rq-card-title">Identificación del documento</div>
              <div className="rq-id-grid">
                <AutoComplete label="Sucursal" value={String(form.id_sucursal || '')}
                  options={sucursales} idField="id_sucursal" displayField="nombre"
                  onChange={v => f('id_sucursal', v)} placeholder="Sucursal..." />
                <AutoComplete label="Tipo Doc." value={form.tipo_documento}
                  options={tiposDocSerie} idField="codigo" displayField="descripcion"
                  selectedField="codigo"
                  onChange={handleTipoDocChange} placeholder="Tipo..." />
                <div className={!form.id_serie ? 'rq-field-required' : ''}>
                  <AutoComplete label="Serie" value={String(form.id_serie || '')}
                    options={seriesFiltradas} idField="id_serie" displayField="descripcion_display"
                    onChange={handleSerieChange} placeholder="Seleccionar serie..." />
                </div>
                <div className="form-row">
                  <label>Número *</label>
                  <input value={form.numero} onChange={e => f('numero', e.target.value)} placeholder="0001-0000001"
                    style={!form.numero ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' } : {}} />
                </div>
                <div className="form-row">
                  <label>F. Emisión *</label>
                  <input type="date" value={form.fecha_emision} onChange={e => f('fecha_emision', e.target.value)}
                    style={!form.fecha_emision ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' } : {}} />
                </div>
                <div className="form-row">
                  <label>F. Entrega</label>
                  <input type="date" value={form.fecha_entrega} onChange={e => f('fecha_entrega', e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── Sección 2: Datos del pedido ── */}
            <div className="rq-card">
              <div className="rq-card-title">Datos del pedido</div>
              {/* Fila 1: Área · Solicitante · Prioridad · Centro de Costo */}
              <div className="rq-g4">
                <div className={!form.id_area ? 'rq-field-required' : ''}>
                  <AutoComplete label="Área" value={String(form.id_area || '')}
                    options={areas} idField="id_area" displayField="descripcion"
                    onChange={v => f('id_area', v)} placeholder="Área..." />
                </div>
                <AutoComplete label="Solicitante" value={String(form.id_solicitante || '')}
                  options={trabajadores} idField="id_trabajador" displayField="display_trab"
                  selectedField="nombres" hideCode={true}
                  onChange={v => f('id_solicitante', v)} placeholder="DNI o nombre..." />
                <AutoComplete label="Prioridad" value={String(form.id_prioridad || '')}
                  options={prioridades} idField="id_prioridad" displayField="descripcion"
                  onChange={v => f('id_prioridad', v)} placeholder="Prioridad..." />
                <AutoComplete label="Centro de Costo" value={String(form.id_centro_costo || '')}
                  options={centrosCosto} idField="id_centro_costo" displayField="display_cc"
                  selectedField="display_cc"
                  onChange={handleCCCabeceraChange} placeholder="Centro de costo..."
                  onAdd={() => { setFormCC({ codigo: '', descripcion: '' }); setModalCC(true); }} addLabel="Centro" />
              </div>
              {/* Fila 2: RUC · Ref1 Tipo · Ref1 Núm · Ref2 Tipo · Ref2 Núm · Observación */}
              <div className="rq-prov-obs-grid">
                <AutoComplete label="RUC / Proveedor" value={String(form.id_proveedor || '')}
                  options={proveedores} idField="id_entidad" displayField="display_prov"
                  hideCode={true}
                  onChange={v => f('id_proveedor', v)} placeholder="RUC o razón social..."
                  onAdd={() => { setFormProv(emptyProv); setModalProv(true); }} addLabel="Proveedor" />
                <AutoComplete label="Ref1 Tipo" value={String(form.id_tipo_doc_ref1 || '')}
                  options={tiposDocComerciales} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => f('id_tipo_doc_ref1', v)} placeholder="Tipo..." />
                <div className="form-row">
                  <label>Ref1 Número</label>
                  <input value={form.num_doc_ref1} onChange={e => f('num_doc_ref1', e.target.value)} placeholder="Nro..." />
                </div>
                <AutoComplete label="Ref2 Tipo" value={String(form.id_tipo_doc_ref2 || '')}
                  options={tiposDocComerciales} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => f('id_tipo_doc_ref2', v)} placeholder="Tipo..." />
                <div className="form-row">
                  <label>Ref2 Número</label>
                  <input value={form.num_doc_ref2} onChange={e => f('num_doc_ref2', e.target.value)} placeholder="Nro..." />
                </div>
                <div className="form-row">
                  <label>Observación</label>
                  <input value={form.observacion1} onChange={e => f('observacion1', e.target.value)} placeholder="Observación general..." />
                </div>
              </div>
            </div>

            {/* ── Sección 3: Artículos solicitados ── */}
            <div className="rq-card rq-card-table">
              <div className="rq-table-header">
                <div className="rq-table-header-left">
                  <span className="rq-table-title">Artículos solicitados</span>
                  <button type="button" className="rq-btn-add-line" onClick={addLinea}>
                    + Agregar ítem
                  </button>
                </div>
                <button type="button" className="rq-btn-create-art" onClick={() => openModalArt(null)}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Crear artículo
                </button>
              </div>
              <div className="rq-table-container">
                <table className="rq-articles">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th style={{ width: 130 }}>Código</th>
                      <th>Descripción</th>
                      <th style={{ width: 70 }}>U.M.</th>
                      <th style={{ width: 86 }}>Cantidad</th>
                      <th style={{ width: 180 }}>Centro de Costo</th>
                      <th style={{ width: 180 }}>Observación</th>
                      <th style={{ width: 38 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="rq-empty">
                          Sin artículos — use "+ Agregar ítem" para comenzar
                        </td>
                      </tr>
                    ) : detalle.map((linea, idx) => {
                      const artSel = linea.id_articulo ? {
                        id_articulo: linea.id_articulo,
                        codigo_interno: linea.articulo_codigo,
                        descripcion: linea.articulo_descripcion,
                        id_unidad: linea.id_unidad,
                        unidad_descripcion: linea.unidad_descripcion,
                        codigo: linea.articulo_codigo,
                        display_linea: [linea.articulo_codigo, linea.articulo_descripcion, linea.unidad_descripcion].filter(Boolean).join(' · '),
                      } : null;
                      return (
                      <tr key={linea._key}>
                        <td className="rq-td-num">{idx + 1}</td>
                        <td className="rq-td-cod">
                          <AutoComplete label="" value={String(linea.id_articulo || '')}
                            idField="id_articulo" displayField={(a) => [a.codigo_interno, a.descripcion, a.unidad_descripcion].filter(Boolean).join(' · ')}
                            selectedField="codigo_interno"
                            hideCode
                            dropdownMinWidth={420}
                            asyncSearch={async (q) => {
                              const r = await articuloService.search(q);
                              return r.data || [];
                            }}
                            minChars={3}
                            initialSelected={artSel}
                            onChange={(v, item) => seleccionarArticuloLinea(linea._key, item)}
                            placeholder="Buscar por código o descripción (mín. 3)..." />
                        </td>
                        <td className="rq-td-desc">
                          <span className="rq-td-text">{linea.articulo_descripcion || ''}</span>
                        </td>
                        <td className="rq-td-um">
                          <span className="rq-td-text">{linea.unidad_descripcion || ''}</span>
                        </td>
                        <td className="rq-td-qty">
                          <input type="number" min="0.0001" step="any" value={linea.cantidad}
                            onChange={e => updateLinea(linea._key, 'cantidad', e.target.value)} />
                        </td>
                        <td className="rq-td-cc">
                          <AutoComplete label="" value={String(linea.id_centro_costo || '')}
                            options={centrosCosto} idField="id_centro_costo" displayField="display_cc"
                            dropdownMinWidth={260}
                            onChange={v => updateLinea(linea._key, 'id_centro_costo', v)}
                            placeholder="Centro..." />
                        </td>
                        <td className="rq-td-obs">
                          <input value={linea.observacion}
                            onChange={e => updateLinea(linea._key, 'observacion', e.target.value)}
                            placeholder="Obs..." />
                        </td>
                        <td className="rq-td-del">
                          <button type="button" className="rq-btn-del" onClick={() => removeLinea(linea._key)} title="Eliminar línea">
                            <TrashIcon />
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Acciones al pie ── */}
          <div className="rq-form-actions">
            <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancelar</button>
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Guardar Cambios' : 'Crear Requerimiento'}
            </button>
          </div>

        </form>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL PROVEEDOR (mismo formulario que Entidades.js)
      ═══════════════════════════════════════════════════════════ */}
      {modalProv && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2>Proveedor / Cliente</h2>
                <span className="modal-header-badge">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
              <button className="modal-close" onClick={() => setModalProv(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveProv}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Tipo de Documento</label>
                  <select value={formProv.tipo_documento} onChange={e => setFormProv(p => ({ ...p, tipo_documento: e.target.value }))}>
                    {TIPOS_DOC_ENTIDAD.map(t => <option key={t.codigo} value={t.codigo}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: formProv.tipo_documento === '6' ? '140px 1fr auto' : '140px 1fr', gap: '0 12px', alignItems: 'flex-end' }}>
                  <div className="form-row">
                    <label>Nro. Documento</label>
                    <input value={formProv.ruc} onChange={e => setFormProv(p => ({ ...p, ruc: e.target.value }))}
                      placeholder={formProv.tipo_documento === '6' ? 'RUC 11 dígitos' : 'Nro. documento'}
                      maxLength={formProv.tipo_documento === '6' ? 11 : 20} />
                  </div>
                  <div className="form-row">
                    <label>Razón Social / Nombre *</label>
                    <input value={formProv.razon_social} onChange={e => setFormProv(p => ({ ...p, razon_social: e.target.value }))} placeholder="Razón social o nombre" />
                  </div>
                  {formProv.tipo_documento === '6' && (
                    <button type="button" className="btn btn-secondary" onClick={handleConsultarRuc}
                      disabled={consultandoRuc} style={{ marginBottom: 18, whiteSpace: 'nowrap' }}>
                      {consultandoRuc
                        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rq-spin"><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
                        : <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>}
                    </button>
                  )}
                </div>
                <div className="form-row">
                  <label>Dirección</label>
                  <input value={formProv.direccion} onChange={e => setFormProv(p => ({ ...p, direccion: e.target.value }))} placeholder="Dirección" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Correo</label>
                    <input value={formProv.correo1} onChange={e => setFormProv(p => ({ ...p, correo1: e.target.value }))} placeholder="correo@ejemplo.com" />
                  </div>
                  <div className="form-row">
                    <label>Teléfono</label>
                    <input value={formProv.telefono_contacto} onChange={e => setFormProv(p => ({ ...p, telefono_contacto: e.target.value }))} placeholder="Teléfono" />
                  </div>
                  <div className="form-row">
                    <label>Celular</label>
                    <input value={formProv.celular} onChange={e => setFormProv(p => ({ ...p, celular: e.target.value }))} placeholder="Celular" />
                  </div>
                </div>
                <div className="form-row">
                  <label>Persona de Contacto</label>
                  <input value={formProv.persona_contacto} onChange={e => setFormProv(p => ({ ...p, persona_contacto: e.target.value }))} placeholder="Nombre del contacto" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalProv(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={savingProv}>
                  {savingProv ? 'Guardando...' : 'Crear Proveedor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL ARTÍCULO (mismo formulario que Articulos.js)
      ═══════════════════════════════════════════════════════════ */}
      {modalArt && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal modal-xl" style={{ zIndex: 10001 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2>Nuevo Artículo</h2>
                <span className="modal-header-badge">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
              <button className="modal-close" onClick={() => setModalArt(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveArt}>
              <div className="art-layout">
                <div className="art-tabs-sidebar">
                  {[{ key: 'general', label: 'General' }, { key: 'clasificacion', label: 'Clasificación' },
                    { key: 'dimensiones', label: 'Dimensiones' }, { key: 'stock', label: 'Stock' }].map(t => (
                    <button key={t.key} type="button"
                      className={`art-tab-btn ${artTab === t.key ? 'active' : ''}`}
                      onClick={() => setArtTab(t.key)}>{t.label}
                    </button>
                  ))}
                </div>
                <div className="art-tab-content">
                  {artTab === 'general' && (
                    <>
                      <div className="form-section-divider" style={{ borderTop: 'none', paddingTop: 0 }}>Identificación</div>
                      <div className="auto-code-row">
                        <div className="form-row" style={{ flex: 1 }}>
                          <label>Código Interno *</label>
                          <input value={formArt.codigo_interno} onChange={e => setArtField('codigo_interno', e.target.value)}
                            placeholder="Código interno" readOnly={autoCode}
                            style={{ ...(autoCode ? { opacity: .65, cursor: 'not-allowed' } : {}), ...(!formArt.codigo_interno ? { boxShadow: '0 0 0 1.5px #ef4444, 0 0 0 4px rgba(239,68,68,0.10)', borderRadius: 8 } : {}) }} />
                        </div>
                        <div className="toggle-switch" onClick={toggleAutoCode}>
                          <div className={`toggle-track ${autoCode ? 'on' : ''}`}><div className="toggle-thumb" /></div>
                          <span className="toggle-label">Auto</span>
                        </div>
                        <div className="form-row" style={{ flex: 1 }}>
                          <label>Código SUNAT</label>
                          <input value={formArt.codigo_sunat} onChange={e => setFormArt(p => ({ ...p, codigo_sunat: e.target.value }))} placeholder="Código SUNAT" />
                        </div>
                      </div>
                      <div className="form-row">
                        <label>Descripción *</label>
                        <input value={formArt.descripcion} onChange={e => setArtField('descripcion', e.target.value)} placeholder="Descripción del artículo" autoFocus
                          style={!formArt.descripcion ? { boxShadow: '0 0 0 1.5px #ef4444, 0 0 0 4px rgba(239,68,68,0.10)', borderRadius: 8 } : undefined} />
                      </div>
                      <div className="form-row">
                        <label>Descripción Alternativa</label>
                        <input value={formArt.descripcion_alt} onChange={e => setFormArt(p => ({ ...p, descripcion_alt: e.target.value }))} placeholder="Descripción alternativa" />
                      </div>
                      <div className="form-section-divider">Contabilidad</div>
                      <div className="form-grid-3">
                        <AutoComplete label="Tipo Existencia *" value={formArt.id_tipo_existencia} options={tiposExistencia} idField="id_tipo_existencia" displayField="descripcion"
                          error={!formArt.id_tipo_existencia}
                          onChange={v => setArtField('id_tipo_existencia', v)} onAdd={() => openMiniModal('tipos-existencia', 'Tipo de Existencia')} addLabel="Tipo de Existencia" placeholder="Buscar tipo de existencia..." />
                        <AutoComplete label="Cuenta Contable *" value={formArt.id_cuenta_contable} options={cuentasContables} idField="id_cuenta_contable" displayField="descripcion"
                          error={!formArt.id_cuenta_contable}
                          onChange={v => setArtField('id_cuenta_contable', v)} onAdd={() => openMiniModal('cuentas-contables', 'Cuenta Contable')} addLabel="Cuenta Contable" placeholder="Buscar cuenta contable..." />
                        <div className="form-row"><label>País de Origen</label><input value={formArt.pais} onChange={e => setFormArt(p => ({ ...p, pais: e.target.value }))} placeholder="País" /></div>
                      </div>
                    </>
                  )}
                  {artTab === 'clasificacion' && (
                    <>
                      <div className="form-section-divider" style={{ borderTop: 'none', paddingTop: 0 }}>Unidad y Modelo</div>
                      <div className="form-grid-2">
                        <AutoComplete label="Unidad de Medida *" value={formArt.id_unidad} options={unidades} idField="id_unidad" displayField="descripcion"
                          error={!formArt.id_unidad}
                          onChange={v => setArtField('id_unidad', v)} onAdd={() => openMiniModal('unidades', 'Unidad de Medida')} addLabel="Unidad" placeholder="Buscar unidad..." />
                        <div className="form-row"><label>Peso</label><input type="number" step="0.01" value={formArt.peso} onChange={e => setFormArt(p => ({ ...p, peso: e.target.value }))} placeholder="0.00" /></div>
                      </div>
                      <div className="form-grid-2">
                        <AutoComplete label="Marca *" value={formArt.id_marca} options={marcas} idField="id_marca" displayField="descripcion"
                          error={!formArt.id_marca}
                          onChange={v => setArtField('id_marca', v)} onAdd={() => openMiniModal('marcas', 'Marca')} addLabel="Marca" placeholder="Buscar marca..." />
                        <AutoComplete label="Modelo *" value={formArt.id_modelo} options={modelos} idField="id_modelo" displayField="descripcion"
                          error={!formArt.id_modelo}
                          onChange={v => setArtField('id_modelo', v)} onAdd={() => openMiniModal('modelos', 'Modelo')} addLabel="Modelo" placeholder="Buscar modelo..." />
                      </div>
                      <div className="form-section-divider">Agrupación</div>
                      <div className="form-grid-2">
                        <AutoComplete label="Familia *" value={formArt.id_familia} options={familias} idField="id_familia" displayField="descripcion"
                          error={!formArt.id_familia}
                          onChange={v => setArtField('id_familia', v)} onAdd={() => openMiniModal('familias', 'Familia')} addLabel="Familia" placeholder="Buscar familia..." />
                        <AutoComplete label="Grupo *" value={formArt.id_grupo} options={grupos} idField="id_grupo" displayField="descripcion"
                          error={!formArt.id_grupo}
                          onChange={v => setArtField('id_grupo', v)} onAdd={() => openMiniModal('grupos', 'Grupo')} addLabel="Grupo" placeholder="Buscar grupo..." />
                      </div>
                      <div className="form-grid-2">
                        <AutoComplete label="Línea *" value={formArt.id_linea} options={lineas} idField="id_linea" displayField="descripcion"
                          error={!formArt.id_linea}
                          onChange={v => setArtField('id_linea', v)} onAdd={() => openMiniModal('lineas', 'Línea')} addLabel="Línea" placeholder="Buscar línea..." />
                        <AutoComplete label="Categoría *" value={formArt.id_categoria} options={categorias} idField="id_categoria" displayField="descripcion"
                          error={!formArt.id_categoria}
                          onChange={v => setArtField('id_categoria', v)} onAdd={() => openMiniModal('categorias', 'Categoría')} addLabel="Categoría" placeholder="Buscar categoría..." />
                      </div>
                    </>
                  )}
                  {artTab === 'dimensiones' && (
                    <>
                      <div className="form-section-divider" style={{ borderTop: 'none', paddingTop: 0 }}>Medidas</div>
                      <div className="form-grid-4">
                        <div className="form-row"><label>Ancho</label><input type="number" step="0.01" value={formArt.ancho} onChange={e => setFormArt(p => ({ ...p, ancho: e.target.value }))} placeholder="0.00" /></div>
                        <div className="form-row"><label>Largo</label><input type="number" step="0.01" value={formArt.largo} onChange={e => setFormArt(p => ({ ...p, largo: e.target.value }))} placeholder="0.00" /></div>
                        <div className="form-row"><label>Grosor</label><input type="number" step="0.01" value={formArt.grosor} onChange={e => setFormArt(p => ({ ...p, grosor: e.target.value }))} placeholder="0.00" /></div>
                        <div className="form-row"><label>Medida</label><input value={formArt.medida} onChange={e => setFormArt(p => ({ ...p, medida: e.target.value }))} placeholder="cm" /></div>
                      </div>
                      <div className="form-grid-2">
                        <div className="form-row"><label>Volumen</label><input type="number" step="0.01" value={formArt.volumen} onChange={e => setFormArt(p => ({ ...p, volumen: e.target.value }))} placeholder="0.00" /></div>
                        <div className="form-row"><label>Área</label><input type="number" step="0.01" value={formArt.area} onChange={e => setFormArt(p => ({ ...p, area: e.target.value }))} placeholder="0.00" /></div>
                      </div>
                    </>
                  )}
                  {artTab === 'stock' && (
                    <>
                      <div className="form-section-divider" style={{ borderTop: 'none', paddingTop: 0 }}>Control de Stock</div>
                      <div className="form-grid-2">
                        <div className="form-row"><label>Stock Mínimo</label><input type="number" step="0.01" value={formArt.stock_minimo} onChange={e => setFormArt(p => ({ ...p, stock_minimo: e.target.value }))} placeholder="0.00" /></div>
                        <div className="form-row"><label>Stock Máximo</label><input type="number" step="0.01" value={formArt.stock_maximo} onChange={e => setFormArt(p => ({ ...p, stock_maximo: e.target.value }))} placeholder="0.00" /></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalArt(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={savingArt}>
                  {savingArt ? 'Creando...' : 'Crear Artículo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MINI MODAL — Creación rápida de subcategorías de artículo
      ═══════════════════════════════════════════════════════════ */}
      {miniModal && (
        <div className="modal-overlay" style={{ zIndex: 10002 }}>
          <div className="modal" style={{ maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2>{miniModal.label}</h2>
                <span className="modal-header-badge">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
                </span>
              </div>
              <button className="modal-close" onClick={() => setMiniModal(null)}>&times;</button>
            </div>
            <form onSubmit={handleMiniSubmit}>
              <div className="modal-body">
                <div className="form-row"><label>Código *</label><input value={miniForm.codigo} onChange={e => setMiniForm(p => ({ ...p, codigo: e.target.value }))} placeholder="Código" autoFocus /></div>
                {miniModal.type === 'unidades' && (
                  <div className="form-row"><label>Código SUNAT</label><input value={miniForm.codigo_sunat} onChange={e => setMiniForm(p => ({ ...p, codigo_sunat: e.target.value }))} placeholder="Código SUNAT" /></div>
                )}
                <div className="form-row"><label>Descripción *</label><input value={miniForm.descripcion} onChange={e => setMiniForm(p => ({ ...p, descripcion: e.target.value }))} placeholder="Descripción" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setMiniModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL CENTRO DE COSTO
      ═══════════════════════════════════════════════════════════ */}
      {modalCC && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2>Centro de Costo</h2>
                <span className="modal-header-badge">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
              <button className="modal-close" onClick={() => setModalCC(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveCC}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Código *</label>
                  <input value={formCC.codigo} onChange={e => setFormCC(p => ({ ...p, codigo: e.target.value }))} placeholder="Ej: CC01" autoFocus />
                </div>
                <div className="form-row">
                  <label>Descripción *</label>
                  <input value={formCC.descripcion} onChange={e => setFormCC(p => ({ ...p, descripcion: e.target.value }))} placeholder="Descripción del centro de costo..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalCC(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={savingCC}>Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL FIRMANTES (PREVIA A IMPRESIÓN)
      ═══════════════════════════════════════════════════════════ */}
      {printTarget && (
        <ModalFirmantes
          trabajadores={trabajadores}
          initialFirmantes={firmantesDefault}
          onConfirm={(slots, saveAsDefault) => {
            if (saveAsDefault) {
              setFirmantesDefault(slots);
              localStorage.setItem(LS_KEY, JSON.stringify(slots));
            }
            doPrint(slots);
          }}
          onCancel={() => setPrintTarget(null)}
        />
      )}

      {/* ── Modal elección Desaprobar ───────────────────────────── */}
      {desaprobarTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Desaprobar Requerimiento</h2>
              <button className="modal-close" onClick={() => setDesaprobarTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#94a3b8', marginBottom: 20, fontSize: 13.5 }}>
                ¿Qué desea hacer con <strong style={{ color: '#e2e8f0' }}>{desaprobarTarget.numero}</strong>?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={() => doDesaprobar('DESAPROBADO')} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  background: 'rgba(248,113,113,0.07)', border: '1px solid rgba(248,113,113,0.25)',
                  borderRadius: 8, color: '#f87171', cursor: 'pointer', textAlign: 'left',
                  font: '600 13px Inter, sans-serif', transition: 'all 0.15s', width: '100%',
                }}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style={{ flexShrink: 0 }}>
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                  <div>
                    <div>Marcar como Desaprobado</div>
                    <div style={{ fontSize: 11, fontWeight: 400, color: '#64748b', marginTop: 3 }}>El requerimiento queda en estado DESAPROBADO</div>
                  </div>
                </button>
                <button onClick={() => doDesaprobar('PENDIENTE')} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)',
                  borderRadius: 8, color: '#fbbf24', cursor: 'pointer', textAlign: 'left',
                  font: '600 13px Inter, sans-serif', transition: 'all 0.15s', width: '100%',
                }}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style={{ flexShrink: 0 }}>
                    <path fillRule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd"/>
                  </svg>
                  <div>
                    <div>Volver a Pendiente</div>
                    <div style={{ fontSize: 11, fontWeight: 400, color: '#64748b', marginTop: 3 }}>El requerimiento regresa al estado PENDIENTE</div>
                  </div>
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDesaprobarTarget(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {alert   && <Alert   type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} confirmLabel={confirm.confirmLabel} confirmStyle={confirm.confirmStyle} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast   && <Toast   message={toast} onClose={() => setToast(null)} />}

      {showBulkDelete && (
        <ConfirmDestructive
          title="Eliminar requerimientos"
          message="Esta acción eliminará todos los requerimientos que se muestran actualmente en la lista (respetando los filtros aplicados)."
          count={filteredRecords.length}
          loading={bulkDeleteLoading}
          onCancel={() => !bulkDeleteLoading && setShowBulkDelete(false)}
          onConfirm={async () => {
            setBulkDeleteLoading(true);
            try {
              const ids = filteredRecords.map(r => r.id_requerimiento);
              const res = await requerimientoService.deleteBulk(ids);
              const { ok = 0, fail = 0, errores = [] } = res.data || {};
              setShowBulkDelete(false);
              fetchRecords();
              if (fail === 0) {
                setToast(`${ok} requerimiento${ok !== 1 ? 's' : ''} eliminado${ok !== 1 ? 's' : ''}`);
              } else {
                setAlert({
                  type: 'warning',
                  message: `Eliminados: ${ok}. Omitidos por tener documentos relacionados: ${fail}.` +
                    (errores.length ? '\n\nPrimeros motivos:\n' + errores.slice(0, 5).join('\n') : ''),
                });
              }
            } catch (err) {
              setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar registros.') });
            } finally {
              setBulkDeleteLoading(false);
            }
          }}
        />
      )}

      {/* ── Modal Ver RQ ── */}
      {viewModal && ReactDOM.createPortal(
        <div style={{ position:'fixed', inset:0, zIndex:9400, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}
          onClick={() => setViewModal(null)}>
          <div style={{ background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:14, width:'min(900px,96vw)', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 48px rgba(0,0,0,0.5)', overflow:'hidden' }}
            onClick={e => e.stopPropagation()}>

            {/* Cabecera modal */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px 12px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:32, height:32, borderRadius:8, background:'rgba(20,184,166,0.15)', border:'1px solid rgba(20,184,166,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" style={{ color:'#2dd4bf' }}><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                </div>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>
                    RQ {viewModal.serie_codigo ? `${viewModal.serie_codigo}-` : ''}{viewModal.numero}
                  </div>
                  <div style={{ fontSize:11, color:'#64748b', marginTop:1 }}>Vista rápida del requerimiento</div>
                </div>
              </div>
              <button onClick={() => setViewModal(null)} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:22, cursor:'pointer', lineHeight:1, padding:'0 4px' }}>×</button>
            </div>

            <div style={{ overflowY:'auto', flex:1, padding:'16px 20px' }}>
              {loadingView && <div style={{ textAlign:'center', color:'#64748b', padding:40 }}>Cargando...</div>}
              {viewData && (() => {
                const { req, atencion } = viewData;
                const fmt = d => d ? d.split('T')[0] : '—';
                const ECOL = { PENDIENTE:'#f59e0b', APROBADO:'#22c55e', DESAPROBADO:'#fb923c', ANULADO:'#64748b', RECHAZADO:'#f87171' };
                const EBG  = { PENDIENTE:'rgba(245,158,11,0.1)', APROBADO:'rgba(34,197,94,0.1)', DESAPROBADO:'rgba(251,146,60,0.1)', ANULADO:'rgba(100,116,139,0.1)', RECHAZADO:'rgba(248,113,113,0.1)' };
                const itemMap = {};
                (atencion.items || []).forEach(it => { itemMap[it.id_detalle] = it; });

                const SecTitle = ({ children, first }) => (
                  <div style={{ display:'flex', alignItems:'center', gap:8, margin: first ? '0 0 8px' : '16px 0 8px' }}>
                    <span style={{ fontSize:9.5, fontWeight:700, color:'#60a5fa', textTransform:'uppercase', letterSpacing:'1px', whiteSpace:'nowrap' }}>{children}</span>
                    <div style={{ flex:1, height:1, background:'rgba(96,165,250,0.2)' }} />
                  </div>
                );
                const Cell = ({ label, value, w }) => (
                  <div style={{ flex: w === 'full' ? '1 1 100%' : `0 0 ${w}px`, minWidth:0, paddingBottom:8 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'.6px', marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:12.5, color:'#e2e8f0', fontWeight:400, lineHeight:1.35, wordBreak:'break-word' }}>{value ?? '—'}</div>
                  </div>
                );
                const Row = ({ children }) => (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0 16px' }}>{children}</div>
                );

                return (<>
                  {/* ── Documento ── */}
                  <SecTitle first>Datos del Documento</SecTitle>
                  <Row>
                    <Cell label="Serie - Número" w={160} value={`${req.serie_codigo ? req.serie_codigo.trim()+'-' : ''}${req.numero}`} />
                    <Cell label="Estado" w={120} value={
                      <span style={{ background:EBG[req.estado], color:ECOL[req.estado], borderRadius:12, padding:'2px 9px', fontSize:11, fontWeight:700 }}>{req.estado}</span>
                    } />
                    <Cell label="F. Emisión"  w={105} value={fmt(req.fecha_emision)} />
                    <Cell label="F. Entrega"  w={105} value={fmt(req.fecha_entrega)} />
                    <Cell label="Tipo Documento" w={140} value={req.tipo_documento} />
                    {req.centro_costo_descripcion && <Cell label="Centro de Costo" w={180} value={req.centro_costo_descripcion} />}
                  </Row>

                  {/* ── Solicitante ── */}
                  <SecTitle>Solicitante</SecTitle>
                  <Row>
                    <Cell label="Sucursal"          w={200} value={req.sucursal_nombre} />
                    <Cell label="Área"               w={160} value={req.area_descripcion} />
                    <Cell label="Prioridad"          w={120} value={req.prioridad_descripcion} />
                    <Cell label="Solicitante"        w={200} value={req.solicitante_nombre} />
                    {req.proveedor_nombre && <Cell label="Proveedor Sugerido" w={220} value={req.proveedor_nombre} />}
                  </Row>

                  {/* ── Referencias ── */}
                  {(req.tipo_doc_ref1_descripcion || req.num_doc_ref1 || req.tipo_doc_ref2_descripcion || req.num_doc_ref2) && (<>
                    <SecTitle>Referencias</SecTitle>
                    <Row>
                      {req.tipo_doc_ref1_descripcion && <Cell label="Tipo Doc. Ref. 1" w={160} value={req.tipo_doc_ref1_descripcion} />}
                      {req.num_doc_ref1 && <Cell label="Nº Doc. Ref. 1" w={140} value={req.num_doc_ref1} />}
                      {req.tipo_doc_ref2_descripcion && <Cell label="Tipo Doc. Ref. 2" w={160} value={req.tipo_doc_ref2_descripcion} />}
                      {req.num_doc_ref2 && <Cell label="Nº Doc. Ref. 2" w={140} value={req.num_doc_ref2} />}
                    </Row>
                  </>)}

                  {/* ── Observaciones ── */}
                  {(req.observacion1 || req.observacion2) && (<>
                    <SecTitle>Observaciones</SecTitle>
                    <Row>
                      {req.observacion1 && <Cell label="Observación 1" w="full" value={req.observacion1} />}
                      {req.observacion2 && <Cell label="Observación 2" w="full" value={req.observacion2} />}
                    </Row>
                  </>)}

                  {/* ── Artículos ── */}
                  <SecTitle>Artículos</SecTitle>
                  <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, overflow:'hidden', marginBottom:4 }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
                      <thead>
                        <tr style={{ background:'rgba(255,255,255,0.04)' }}>
                          {['#','Código','Descripción','Solicitado','En OC','Pendiente','Estado'].map((h,i) => (
                            <th key={h} style={{ padding:'7px 10px', color:'#64748b', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'.4px', textAlign:i>=3?'center':'left', borderBottom:'1px solid rgba(255,255,255,0.06)', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(req.detalle || []).map((d, i) => {
                          const at   = itemMap[d.id_detalle] || {};
                          const enOC = parseFloat(at.cantidad_en_oc ?? 0);
                          const pend = Math.max(0, parseFloat(d.cantidad) - enOC);
                          const est  = at.estado_atencion || 'PENDIENTE';
                          const estCfg = {
                            PENDIENTE: { color:'#fbbf24', bg:'rgba(251,191,36,0.1)', label:'Pendiente' },
                            PARCIAL:   { color:'#fb923c', bg:'rgba(251,146,60,0.1)', label:'Parcial'   },
                            ATENDIDO:  { color:'#22c55e', bg:'rgba(34,197,94,0.1)',  label:'Atendido'  },
                          }[est] || { color:'#fbbf24', bg:'rgba(251,191,36,0.1)', label:'Pendiente' };
                          return (
                            <tr key={d.id_detalle} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                              <td style={{ padding:'6px 10px', color:'#475569', textAlign:'center', fontSize:10.5 }}>{i+1}</td>
                              <td style={{ padding:'6px 10px', color:'#94a3b8', fontFamily:'monospace', fontSize:10.5 }}>{d.articulo_codigo || '—'}</td>
                              <td style={{ padding:'6px 10px', color:'#e2e8f0', fontWeight:500 }}>{d.articulo_descripcion || '—'}</td>
                              <td style={{ padding:'6px 10px', textAlign:'center', color:'#e2e8f0', fontWeight:600 }}>{parseFloat(d.cantidad).toLocaleString('es-PE',{maximumFractionDigits:4})}</td>
                              <td style={{ padding:'6px 10px', textAlign:'center', color: enOC > 0 ? '#22c55e' : '#475569', fontWeight:600 }}>{enOC.toLocaleString('es-PE',{maximumFractionDigits:4})}</td>
                              <td style={{ padding:'6px 10px', textAlign:'center', color: pend > 0 ? '#fb923c' : '#22c55e', fontWeight:700 }}>{pend.toLocaleString('es-PE',{maximumFractionDigits:4})}</td>
                              <td style={{ padding:'6px 10px', textAlign:'center' }}>
                                <span style={{ background:estCfg.bg, color:estCfg.color, borderRadius:12, padding:'2px 8px', fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}>{estCfg.label}</span>
                              </td>
                            </tr>
                          );
                        })}
                        {(req.detalle || []).length === 0 && (
                          <tr><td colSpan={7} style={{ textAlign:'center', color:'#475569', padding:20, fontStyle:'italic' }}>Sin artículos</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Aprobación ── */}
                  {(req.usuario_aprobacion || req.fecha_aprobacion || req.observacion_aprobacion) && (<>
                    <SecTitle>Aprobación</SecTitle>
                    <Row>
                      {req.usuario_aprobacion  && <Cell label="Aprobado por"      w={180} value={req.usuario_aprobacion} />}
                      {req.fecha_aprobacion    && <Cell label="Fecha Aprobación"   w={140} value={fmt(req.fecha_aprobacion)} />}
                      {req.observacion_aprobacion && <Cell label="Obs. Aprobación" w={300} value={req.observacion_aprobacion} />}
                    </Row>
                  </>)}

                  {/* ── Auditoría ── */}
                  <SecTitle>Auditoría</SecTitle>
                  <Row>
                    <Cell label="Creado por"       w={160} value={req.usuario_creacion} />
                    <Cell label="Fecha Creación"   w={140} value={fmt(req.fecha_creacion)} />
                    {req.usuario_modificacion && <Cell label="Modificado por"  w={160} value={req.usuario_modificacion} />}
                    {req.fecha_modificacion   && <Cell label="Últ. Modificación" w={140} value={fmt(req.fecha_modificacion)} />}
                  </Row>
                </>);
              })()}
            </div>

            <div style={{ padding:'11px 20px', borderTop:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setViewModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal Atención OC ── */}
      {atencionOCModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
          onClick={() => setAtencionOCModal(null)}>
          <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, width: 'min(820px,96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 48px rgba(0,0,0,0.5)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>
                  Atención OC — RQ {atencionOCModal.serie_codigo ? `${atencionOCModal.serie_codigo}-` : ''}{atencionOCModal.numero}
                </div>
                <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                  Estado de atención por ítem en órdenes de compra
                </div>
              </div>
              <button onClick={() => setAtencionOCModal(null)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', padding: '14px 20px', flex: 1 }}>
              {loadingAtencionOC && <div style={{ textAlign: 'center', color: '#64748b', padding: 32 }}>Cargando...</div>}
              {atencionOCData && atencionOCData.items.map(item => {
                const ocs = (atencionOCData.ocs || []).filter(o => o.id_requerimiento_detalle === item.id_detalle);
                const est = item.estado_atencion;
                const estColor = est === 'ATENDIDO' ? '#22c55e' : est === 'PARCIAL' ? '#fb923c' : '#fbbf24';
                const estBg    = est === 'ATENDIDO' ? 'rgba(34,197,94,0.12)' : est === 'PARCIAL' ? 'rgba(251,146,60,0.12)' : 'rgba(251,191,36,0.12)';
                const estLabel = est === 'ATENDIDO' ? 'Atendido' : est === 'PARCIAL' ? 'Parcial' : 'Pendiente';
                return (
                  <div key={item.id_detalle} style={{ marginBottom: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, overflow: 'hidden' }}>
                    {/* Cabecera del ítem */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', borderBottom: ocs.length > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#e2e8f0' }}>
                          {item.articulo_codigo ? `[${item.articulo_codigo}] ` : ''}{item.articulo_descripcion || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          {item.unidad_descripcion || '—'} &nbsp;·&nbsp;
                          Solicitado: <strong style={{ color: '#94a3b8' }}>{parseFloat(item.cantidad_solicitada).toFixed(4)}</strong> &nbsp;·&nbsp;
                          En OC: <strong style={{ color: '#94a3b8' }}>{parseFloat(item.cantidad_en_oc).toFixed(4)}</strong>
                        </div>
                      </div>
                      <span style={{ background: estBg, color: estColor, borderRadius: 20, padding: '3px 11px', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {estLabel}
                      </span>
                    </div>

                    {/* OCs del ítem */}
                    {ocs.length > 0 && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                        <thead>
                          <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
                            {['Sucursal', 'Serie-Número', 'Fecha', 'RUC Proveedor', 'Proveedor', 'Estado', 'Cant.', ''].map(h => (
                              <th key={h} style={{ padding: '6px 10px', color: '#64748b', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px', textAlign: h === 'Cant.' ? 'right' : 'left', borderBottom: '1px solid rgba(255,255,255,0.05)', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {ocs.map((oc, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                              <td style={{ padding: '6px 10px', color: '#cbd5e1' }}>{oc.sucursal || '—'}</td>
                              <td style={{ padding: '6px 10px', color: '#60a5fa', fontWeight: 600 }}>{oc.serie_codigo ? `${oc.serie_codigo}-` : ''}{oc.numero}</td>
                              <td style={{ padding: '6px 10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{oc.fecha_emision?.split('T')[0] || '—'}</td>
                              <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{oc.proveedor_ruc || '—'}</td>
                              <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{oc.proveedor_nombre || '—'}</td>
                              <td style={{ padding: '6px 10px' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 12,
                                  background: oc.estado === 'APROBADO' ? 'rgba(34,197,94,0.12)' : 'rgba(251,191,36,0.12)',
                                  color: oc.estado === 'APROBADO' ? '#22c55e' : '#fbbf24' }}>
                                  {oc.estado}
                                </span>
                              </td>
                              <td style={{ padding: '6px 10px', color: '#e2e8f0', textAlign: 'right', fontWeight: 600 }}>{parseFloat(oc.cantidad).toFixed(4)}</td>
                              <td style={{ padding: '4px 8px' }}>
                                <button onClick={() => previewOC(oc.id_orden)}
                                  title="Ver OC en nueva pestaña"
                                  style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', fontSize:10, fontWeight:600, borderRadius:5, border:'1px solid rgba(96,165,250,0.3)', background:'rgba(96,165,250,0.08)', color:'#60a5fa', cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.12s' }}
                                  onMouseEnter={e => { e.currentTarget.style.background='rgba(96,165,250,0.18)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background='rgba(96,165,250,0.08)'; }}>
                                  <svg viewBox="0 0 20 20" fill="currentColor" width="10" height="10"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                                  Ver OC
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {ocs.length === 0 && (
                      <div style={{ padding: '10px 14px', fontSize: 11.5, color: '#475569', fontStyle: 'italic' }}>
                        Sin órdenes de compra generadas para este ítem.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setAtencionOCModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL SEGUIMIENTO — con tabs (estilo Artículos)
      ═══════════════════════════════════════════════════════════ */}
      {seguimiento && (() => {
        const counts = {
          oc:          (seguimiento.ocs || []).length,
          pe_origen:   (seguimiento.pes_origen || []).length,
          gr:          (seguimiento.grs || []).length,
          pe_destino:  (seguimiento.pes_destino || []).length,
        };
        const tabs = [
          { key: 'oc',         label: 'Orden de Compra',      color: '#60a5fa', count: counts.oc },
          { key: 'pe_origen',  label: 'PE Otros Almacenes',   color: '#22c55e', count: counts.pe_origen },
          { key: 'gr',         label: 'Guías de Remisión',    color: '#a78bfa', count: counts.gr },
          { key: 'pe_destino', label: 'PE Almacén Solicitante', color: '#fb923c', count: counts.pe_destino },
        ];
        const loading = loadingSeguimiento || seguimiento.loading;
        return (
          <div className="modal-overlay">
            <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>Seguimiento del Requerimiento</h2>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                    {seguimiento.requerimiento ? `${seguimiento.requerimiento.serie_codigo || 'RQ'}-${seguimiento.requerimiento.numero}` : `RQ #${seguimiento.id_rq}`}
                  </div>
                </div>
                <button className="modal-close" onClick={() => setSeguimiento(null)}>×</button>
              </div>

              {loading ? (
                <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Cargando trazabilidad...</div>
              ) : (
                <div className="art-layout">
                  <div className="art-tabs-sidebar">
                    {tabs.map(t => (
                      <button key={t.key} type="button"
                        className={`art-tab-btn ${segTab === t.key ? 'active' : ''}`}
                        onClick={() => setSegTab(t.key)}
                        style={{ position: 'relative' }}>
                        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: t.color, marginRight: 8, verticalAlign: 'middle' }} />
                        {t.label}
                        <span style={{ marginLeft: 8, padding: '1px 6px', fontSize: 10, borderRadius: 10, background: t.count > 0 ? t.color : 'rgba(148,163,184,0.15)', color: t.count > 0 ? '#0f1b2d' : '#64748b', fontWeight: 700 }}>{t.count}</span>
                      </button>
                    ))}
                  </div>

                  <div className="art-tab-content">
                    {segTab === 'oc' && (
                      <SeguimientoTabla
                        color="#60a5fa"
                        empty="No se han generado OCs a partir de este RQ"
                        rows={seguimiento.ocs || []}
                        columns={[
                          { label: 'N°', render: r => <a href={`/orden-compra/listar?id=${r.id_orden}`} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 700 }}>{r.serie_codigo ? `${r.serie_codigo}-${r.numero}` : `OC-${r.numero}`}</a>, w: 120 },
                          { label: 'Fecha', render: r => (r.fecha_emision || '').slice(0, 10), w: 100 },
                          { label: 'Proveedor', render: r => r.proveedor_nombre || '—' },
                          { label: 'Sucursal', render: r => r.sucursal_nombre || '—', w: 130 },
                          { label: 'Estado', render: r => <EstadoBadge estado={r.estado} />, w: 100 },
                        ]}
                      />
                    )}
                    {segTab === 'pe_origen' && (
                      <SeguimientoTabla
                        color="#22c55e"
                        empty="Aún no se ha registrado recepción en otro almacén (o el ingreso fue directo al solicitante)"
                        rows={seguimiento.pes_origen || []}
                        columns={[
                          { label: 'N°', render: r => <a href={`/almacen/parte-entrada?id=${r.id_parte_entrada}`} target="_blank" rel="noopener noreferrer" style={{ color: '#22c55e', fontWeight: 700 }}>PE-{r.numero}</a>, w: 100 },
                          { label: 'Fecha', render: r => (r.fecha_operacion || '').slice(0, 10), w: 100 },
                          { label: 'Almacén', render: r => r.almacen_nombre || '—' },
                          { label: 'Sucursal', render: r => r.sucursal_nombre || '—', w: 130 },
                          { label: 'Origen', render: r => r.oc_numero ? <span style={{ color: '#60a5fa', fontSize: 11 }}>OC-{r.oc_numero}</span> : r.gr_numero ? <span style={{ color: '#a78bfa', fontSize: 11 }}>GR-{r.gr_numero}</span> : '—', w: 110 },
                        ]}
                      />
                    )}
                    {segTab === 'gr' && (
                      <SeguimientoTabla
                        color="#a78bfa"
                        empty="No se han generado guías de remisión"
                        rows={seguimiento.grs || []}
                        columns={[
                          { label: 'N°', render: r => <a href={`/distribucion/guias-remision?id=${r.id_guia}`} target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', fontWeight: 700 }}>{r.serie_codigo ? `${r.serie_codigo}-${r.numero}` : `GR-${r.numero}`}</a>, w: 120 },
                          { label: 'Fecha', render: r => (r.fecha_guia || '').slice(0, 10), w: 100 },
                          { label: 'Origen', render: r => r.almacen_origen_nombre || '—' },
                          { label: 'Destino', render: r => <span>{r.almacen_destino_nombre || '—'}{r.sucursal_destino_nombre ? <span style={{ fontSize: 10, color: '#64748b', display: 'block' }}>{r.sucursal_destino_nombre}</span> : null}</span> },
                          { label: 'Estado', render: r => <EstadoBadge estado={r.estado} />, w: 110 },
                        ]}
                      />
                    )}
                    {segTab === 'pe_destino' && (
                      <SeguimientoTabla
                        color="#fb923c"
                        empty="Todavía no se ha registrado el ingreso en el almacén solicitante"
                        rows={seguimiento.pes_destino || []}
                        columns={[
                          { label: 'N°', render: r => (
                            <span>
                              <a href={`/almacen/parte-entrada?id=${r.id_parte_entrada}`} target="_blank" rel="noopener noreferrer" style={{ color: '#fb923c', fontWeight: 700 }}>PE-{r.numero}</a>
                              {r.entrega_directa && <span title="Entrega directa del proveedor al almacén solicitante" style={{ marginLeft: 6, padding: '1px 5px', fontSize: 9, fontWeight: 700, borderRadius: 4, background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}>DIRECTO</span>}
                            </span>
                          ), w: 140 },
                          { label: 'Fecha', render: r => (r.fecha_operacion || '').slice(0, 10), w: 100 },
                          { label: 'Almacén', render: r => r.almacen_nombre || '—' },
                          { label: 'Sucursal', render: r => r.sucursal_nombre || '—', w: 130 },
                          { label: 'Origen', render: r => r.oc_numero ? <span style={{ color: '#60a5fa', fontSize: 11 }}>OC-{r.oc_numero}</span> : r.gr_numero ? <span style={{ color: '#a78bfa', fontSize: 11 }}>GR-{r.gr_numero}</span> : '—', w: 110 },
                        ]}
                      />
                    )}
                  </div>
                </div>
              )}

              <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setSeguimiento(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════
          MODAL SELECTOR — Resumen / Detallado
      ═══════════════════════════════════════════════════════════ */}
      {segChoice && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Ver Seguimiento</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {segChoice.serie_codigo ? `${segChoice.serie_codigo}-${segChoice.numero}` : `RQ-${segChoice.numero}`}
                </div>
              </div>
              <button className="modal-close" onClick={() => setSegChoice(null)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 0, marginBottom: 18 }}>
                Elija el modo de visualización de la trazabilidad:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button type="button" onClick={verSegResumen}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.25)',
                    borderRadius: 10, color: '#e2e8f0', cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.08)'; }}
                >
                  <div style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 8, background: 'rgba(96,165,250,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="0 0 20 20" fill="#60a5fa" width="18" height="18"><path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 9a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 14a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#93c5fd' }}>Resumen</div>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>Listado de documentos por tipo (OC, PE, GR)</div>
                  </div>
                </button>
                <button type="button" onClick={verSegDetallado}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.25)',
                    borderRadius: 10, color: '#e2e8f0', cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,146,60,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(251,146,60,0.08)'; }}
                >
                  <div style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 8, background: 'rgba(251,146,60,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="0 0 20 20" fill="#fb923c" width="18" height="18"><path d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v2H5V5zm0 4h10v2H5V9zm0 4h6v2H5v-2z"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#fdba74' }}>Detallado</div>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>Por artículo: en qué OC, PE y GR aparece y con qué cantidad</div>
                  </div>
                </button>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSegChoice(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* El reporte detallado abre en nueva pestaña — ver /requerimientos/seguimiento-detallado/:id */}
    </Layout>
  );
};


const EstadoBadge = ({ estado }) => {
  const map = {
    PENDIENTE:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    APROBADO:    { color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    DESAPROBADO: { color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
    ANULADO:     { color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
    GENERADO:    { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
    TRASLADADO:  { color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  };
  const c = map[estado] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' };
  return <span style={{ background: c.bg, color: c.color, padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>{estado || '—'}</span>;
};

const SeguimientoTabla = ({ color, rows, columns, empty }) => {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 13, fontStyle: 'italic', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(148,163,184,0.15)', borderRadius: 10 }}>
        {empty}
      </div>
    );
  }
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'rgba(255,255,255,0.03)', color: '#94a3b8', textAlign: 'left', borderBottom: `2px solid ${color}` }}>
            {columns.map((c, i) => (
              <th key={i} style={{ padding: '10px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', width: c.w }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
              {columns.map((c, j) => (
                <td key={j} style={{ padding: '10px 12px', color: '#cbd5e1' }}>{c.render(r)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Requerimientos;
